import { TutorPanel } from '../ui/tutor-panel.js';
import { CampCoach } from '../ui/camp-coach.js';
import {
  AchievementRejectedSchema,
  AchievementsEventSchema,
  AchievementUnlockedSchema,
  getAchievement,
  CinematicEventSchema,
  QuestCompletedSchema,
  titleOf,
  type FriendPositions,
  type MobDefinition,
  TUTOR_MOB_ID,
  TutorSkipOfferSchema,
  TutorSkipRejectedSchema,
  TUTORIAL_ZONE_ID,
  withinTutorReach,
} from '@shefira/shared';
import { Client, type Room } from '@colyseus/sdk';
import {
  type BiomeId,
  AuctionAccessEventSchema,
  AUCTION_SIGN_MOB_ID,
  auctionBiomeOf,
  auctionHouseAt,
  auctionHouseInReach,
  auctionHouseNear,
  AdminEventSchema,
  AdminRejectedSchema,
  type AdminPlayers,
  AdminPlayersSchema,
  AdminResultSchema,
  BIOMES,
  COMPANY_QUARTER,
  biomeAt,
  BossImpactEventSchema,
  BossPhaseEventSchema,
  BossRespawnEventSchema,
  BossTelegraphEventSchema,
  bossOf,
  buildCollisionGrid,
  DEFAULT_ZONE_ID,
  exitBuildingAt,
  gateApproach,
  gateAt,
  SHEFIRA_MOB_ID,
  CastEventSchema,
  type CastRejected,
  CastRejectedSchema,
  CombatLockEventSchema,
  MountCastEventSchema,
  AnnouncementSchema,
  ServerRestartEventSchema,
  ChatBroadcastSchema,
  InspectionSchema,
  InspectRejectedSchema,
  ChatRejectedSchema,
  ChestOpenedSchema,
  type ChestPlacement,
  ChestRejectedSchema,
  ChestsEventSchema,
  CLOSE_CODE_SERVER_RESTART,
  CLOSE_CODE_SESSION_REPLACED,
  CLOSE_CODE_ZONE_CHANGE,
  type CollisionGrid,
  DeathEventSchema,
  type Direction,
  DIRECTIONS,
  eyeHex,
  getCompanion,
  getItem,
  isBetterThanWorn,
  isRareMob,
  BAIT_REACH,
  getSpell,
  getZone,
  hairHex,
  skinHex,
  HitEventSchema,
  MobAggroEventSchema,
  MobShotEventSchema,
  type InventoryRejected,
  InventoryRejectedSchema,
  InventoryStateSchema,
  LevelUpEventSchema,
  lookTints,
  MobIdSchema,
  MOBS,
  ProfileEventSchema,
  ProgressEventSchema,
  RENDER_ZOOM,
  ResourceEventSchema,
  SpellBarRejectedSchema,
  SpellsEventSchema,
  StatusEventSchema,
  isPlayerStatus,
  TILE_SIZE,
  ZoneChangeEventSchema,
  MoveRejectedEventSchema,
  terrainFieldOf,
  type ZoneDefinition,
  SMITH_MOB_ID,
  RUNESMITH_MOB_ID,
  ReforgeRejectedSchema,
  ReforgeResultSchema,
  withinRunesmithReach,
  isInteractiveMob,
  MERCHANT_MOB_ID,
  withinEncounterReach,
  BOUNTY_REACH,
  WANTED_BOARD_MOB_ID,
  SMELTER_MOB_ID,
  FuseRejectedSchema,
  FuseResultSchema,
  ALCHEMIST_MOB_ID,
  BrewRejectedSchema,
  BrewResultSchema,
  withinAlchemistReach,
  withinSmelterReach,
  SellRejectedSchema,
  BuyRejectedSchema,
  BuyResultSchema,
  SellResultSchema,
  withinMerchantReach,
  STASH_MOB_ID,
  StashRejectedSchema,
  StashResultSchema,
  StashStateSchema,
  withinStashReach,
  type VisibleSlot,
  type PieceRarities,
  AURA_SLOTS,
  unpackRarities,
  UpgradeRejectedSchema,
  UpgradeResultSchema,
  withinSmithReach,
  portalById,
  portalInReach,
  PORTAL_REACH,
  QUEST_REACH,
  PORTAL_PROP_ID,
  PROPS,
  footprintOf,
  TELEPORT_COMBAT_LOCK_MS,
  TeleportedSchema,
  TravelRejectedSchema,
  TravelStateSchema,
  type TravelState,
  type ZonePortal,
} from '@shefira/shared';
import Phaser from 'phaser';

import { CharacterView } from '../game/character-view.js';
import { STEADY_GLIDE_BELOW_TPS, steadyGlide } from '../game/steady-glide.js';
import { DropView } from '../game/drop-view.js';
import { PixelText, SMALL_GLYPH_HEIGHT, SMALL_GLYPH_WIDTH } from '../game/pixel-text.js';
import { NightShade } from '../game/night-shade.js';
import { WeatherLayer } from '../game/weather-layer.js';
import { MoveMarker } from '../game/move-marker.js';
import { LocalMovement } from '../game/local-movement.js';
import { nearestWithin, nextTarget, type Placed } from '../game/keyboard-targeting.js';
import { isTypingTarget, KeyboardWalk, walkDirectionOf } from '../game/keyboard-walk.js';
import { TutorialPointer } from '../game/tutorial-pointer.js';
import { DenGuide } from '../game/den-guide.js';
import { wayTo } from '../game/summit-way.js';
import { BossDefeatTip, bossDefeatTipSeen } from '../ui/boss-defeat-tip.js';
import { puffSmoke } from '../game/smoke-puff.js';
import { playLevelUpBurst } from '../game/level-up-burst.js';
import { CRIT_COLOR, CRIT_SCALE, isHeavyBlow, Juice, spellShake } from '../game/juice.js';
import { cameraBounds } from '../game/viewport.js';
import { lootBurst } from '../game/loot-beam.js';
import { playSpellFx } from '../game/spell-effects.js';
import { flyProjectile } from '../game/projectiles.js';
import { RARE_COLOUR } from '../game/rare-mark.js';
import { ChestLayer } from '../game/chests.js';
import { BeforeTheFire, storyName } from '../game/before-the-fire.js';
import { drawProps, type PropImage } from '../game/props.js';
import { FootstepTrails } from '../game/footstep-trails.js';
import { WorldEditor } from '../editor/world-editor.js';
import { AdminPanel } from '../ui/admin-panel.js';
import { drawTerrain } from '../game/terrain.js';
import {
  clearSession,
  saveSession,
  serverHttpUrl,
  serverWsUrl,
  type StoredSession,
} from '../net/session.js';
import { waitForServer } from '../game/server-restart.js';
import { RestartBanner } from '../ui/restart-banner.js';
import { hasLinkNotice } from '../net/sign-in.js';
import { ProfilePanel } from '../ui/profile-panel.js';
import { BestiaryPanel } from '../ui/bestiary-panel.js';
import {
  REGISTER_REMINDER_QUEST,
  remindGuestToRegister,
  setRegistrationOpener,
} from '../ui/guest-reminder.js';
import { AchievementToast } from '../ui/achievement-toast.js';
import { QuestClient } from '../ui/quest-client.js';
import { RefusalBanner } from '../ui/refusal-banner.js';
import { WorldEventClient } from '../ui/world-event-client.js';
import { listenRareMobs } from '../ui/rare-mob-cue.js';
import { BountyClient } from '../ui/bounty-client.js';
import { EncounterClient } from '../ui/encounter-client.js';
import type { QuestTarget } from '../ui/quest-log.js';
import { ACHIEVEMENT_REFUSAL_TEXT, achievementNotice } from '../ui/achievement-text.js';
import { OnlineCount } from '../ui/online-count.js';
import { ChatPanel } from '../ui/chat.js';
import { bubbleText } from '../ui/chat-links.js';
import { InspectPanel } from '../ui/inspect-panel.js';
import { parseChatLine } from '../ui/chat-commands.js';
import { SocialClient } from '../ui/social-client.js';
import { actionKeyLabel, isAction } from '../settings/keybindings.js';
import { mouseButtonCode } from '../settings/mouse-buttons.js';
import { mousePressOf, PLAYER_PRESS_MS, PLAYER_PRESS_SLOP_PX } from '../game/player-press.js';
import { preference } from '../settings/preferences.js';
import { placeLine } from '../ui/place-text.js';
import { uiFormat, uiText } from '../ui/text.js';
import { language } from '../i18n/language.js';
import { serverText } from '../i18n/server-text.js';
import { InventoryPanel } from '../ui/inventory-panel.js';
import { SmithPanel } from '../ui/smith-panel.js';
import { RunesmithPanel } from '../ui/runesmith-panel.js';
import { MerchantPanel } from '../ui/merchant-panel.js';
import { StashPanel } from '../ui/stash-panel.js';
import { ShopPanel } from '../ui/shop-panel.js';
import { isNativeApp } from '../platform/native.js';
import { AuctionPanel } from '../ui/auction-panel.js';
import { LadderPanel } from '../ui/ladder-panel.js';
import { closeCodeText } from '../ui/moderation-text.js';
import type { AtlasFrame } from '../ui/doll-preview.js';
import { consumeShopReturn } from '../net/shop.js';
import { FusionPanel } from '../ui/fusion-panel.js';
import { AlchemyPanel } from '../ui/alchemy-panel.js';
import { Minimap, type MinimapDot, type MinimapMarker } from '../ui/minimap.js';
import { QuickPotion } from '../ui/quick-potion.js';
import { SpellBar } from '../ui/spell-bar.js';
import { SpellbookPanel } from '../ui/spellbook-panel.js';
import { TouchHud } from '../ui/touch-hud.js';
import { isTouch } from '../ui/touch.js';
import { bottomHudHeight, onBottomHudResize } from '../ui/bottom-hud.js';
import { WorldMap } from '../ui/world-map.js';
import { mapPlayers } from '../ui/world-map-players.js';
import { TravelPanel } from '../ui/travel-panel.js';
import { CombatFrame } from '../ui/combat-frame.js';
import { LowHealthAlert } from '../ui/low-health-alert.js';
import { PingMeter } from '../ui/ping-meter.js';
import { BossBar, type BossBarState } from '../ui/boss-bar.js';
import { TargetFrame, type TargetFrameState } from '../ui/target-frame.js';
import { BossEffects } from '../game/boss-effects.js';
import { VillageSign } from '../ui/village-sign.js';
import { TELEPORT_FLAME_SHEET, TeleportVeil } from '../game/teleport-veil.js';
import { CinematicPlayer } from '../game/cinematics/cinematic-player.js';
import { AerieClient } from '../game/aerie/aerie-client.js';
import { playTeleport } from '../ui/sfx.js';
import { cue, cueAt, inventoryCue, musicFor, SFX_KEYS } from '../audio/cues.js';
import { playMusic, preloadSounds } from '../audio/sound.js';
import { showDiscordButton } from '../ui/discord.js';
import { showSettingsPanel } from '../ui/settings-panel.js';
import { showSoundToggle } from '../ui/sound-toggle.js';
import { type CssAtlas, loadSpriteAtlas } from '../assets/dom-atlas.js';
import { loadLazyAtlases, MANIFEST_CACHE_KEY } from '../assets/manifest-loader.js';
import { hideLoader } from '../ui/loader.js';
import type {
  AssetManifest,
  InventoryState,
  ProgressEvent,
  QuestMarker,
  Rarity,
} from '@shefira/shared';

/**
 * The playable world: ground, players, and click-to-move.
 *
 * The server owns every position. A click is only a request; the character
 * moves when the server's state says it moved, which is why nothing here
 * writes to a position directly.
 */

export interface WorldSceneData {
  manifest: AssetManifest;
  session: StoredSession;
}

interface NetPlayer {
  id: string;
  name: string;
  x: number;
  y: number;
  dir: string;
  anim: string;
  classId: string;
  hp: number;
  maxHp: number;
  level: number;
  helmet: string;
  chest: string;
  weapon: string;
  offhand: string;
  /** Smith's level of each visible piece (ADR 0013), 0 when bare or +0. */
  helmetLevel: number;
  chestLevel: number;
  weaponLevel: number;
  offhandLevel: number;
  glovesLevel: number;
  bootsLevel: number;
  /** Average upgrade level of the outfit: the halo's level. */
  aura: number;
  /** Packed rarity of each real piece worn (`unpackRarities`). */
  rarities: number;
  gloves: string;
  boots: string;
  wings: string;
  /** Footstep trail template id (ADR 0073), empty for none. */
  trail: string;
  hair: string;
  eyes: string;
  hairStyle: string;
  skin: string;
  /** `male` or `female` (shared `BODY_SHAPES`). */
  body: string;
  /** Companion id of the mount ridden, empty on foot. */
  mount: string;
  /** Walking speed from the boots, whole percent (ADR 0059); what the local walk is predicted at. */
  speedBonus: number;
  /** A game master (ADR 0025): `[GM]` and a violet plate. */
  gm: boolean;
  /** A game master out of sight (ADR 0025); only ever sent to themselves. */
  vanished: boolean;
  /** The guild's tag (ADR 0036), empty without one. */
  guild: string;
  /** The achievement whose title is worn (ADR 0046), empty for none. */
  title: string;
  /** Away from the keyboard (ADR 0080). */
  away: boolean;
}

/** A pet out in the zone; keyed by its owner's session id. */
interface NetCompanion {
  companionId: string;
  ownerId: string;
  x: number;
  y: number;
  dir: string;
  anim: string;
}

interface NetMob {
  id: string;
  mobId: string;
  x: number;
  y: number;
  dir: string;
  anim: string;
  hp: number;
  maxHp: number;
  /** Strikes only when struck (ADR 0058), as the server decided for this one. */
  passive?: boolean;
}

interface NetDrop {
  id: string;
  templateId: string;
  quantity: number;
  x: number;
  y: number;
  /** About to be swept away: it blinks. */
  fading: boolean;
}

interface NetMap<T> {
  size: number;
  get(key: string): T | undefined;
  has(key: string): boolean;
  forEach(callback: (value: T, key: string) => void): void;
}

interface ZoneSchema {
  zoneId: string;
  /** Absent until the first patch, like the maps. */
  online?: number;
  players: NetMap<NetPlayer>;
  mobs: NetMap<NetMob>;
  drops: NetMap<NetDrop>;
  companions: NetMap<NetCompanion>;
}

/** What the scene needs to draw any moving thing, player or monster. */
interface Entity {
  kind: 'player' | 'mob' | 'pet';
  /** A slow walker's pace, in tiles per second: drawn sliding steadily (`steadyGlide`). */
  glideTilesPerSecond?: number;
  bodyKey: string;
  name: string;
  x: number;
  y: number;
  dir: string;
  anim: string;
  hp: number;
  maxHp: number;
  /** Visible slots worn, by template id; absent for monsters. */
  appearance?: {
    helmet: string;
    chest: string;
    weapon: string;
    offhand: string;
    gloves: string;
    boots: string;
    /** Wings (ADR 0053): players only, a townsperson's outfit has none. */
    wings?: string;
  };
  /** Smith's level of each visible piece; players only. */
  upgrades?: Partial<Record<VisibleSlot, number>>;
  /** The halo's level; players only. */
  aura?: number;
  /** Rarity of each real piece worn (ADR 0061); players and dressed townsfolk. */
  rarities?: PieceRarities;
  /** Hair and eye colour names (shared `look.ts`); absent for monsters. */
  look?: { hair: string; eyes: string; hairStyle: string; skin: string; body: string };
  /** Companion id of the mount ridden; players only. */
  mount?: string;
  /** A game master's plate; players only. */
  gm?: boolean;
  /** A game master out of sight, drawn faded; players only. */
  vanished?: boolean;
  /** The guild's tag (ADR 0036), empty without one. */
  guild?: string;
  /** The worn title's achievement id (ADR 0046). */
  title?: string;
  /** Away from the keyboard (ADR 0080); players only. */
  away?: boolean;
  /** Shown before the name; absent for the peaceful. */
  level?: number;
  /** A villager or a deer: named in a friendly colour, never attacked. */
  peaceful?: boolean;
  /** A townsperson who answers a click (the smith): named apart from the passers-by. */
  interactive?: boolean;
  /** An elite's rarity (ADR 0023): its name takes the colour of the gear of that rarity. */
  rarity?: Rarity;
  /** A zone boss (ADR 0024): named in a red brighter than any other enemy's. */
  boss?: boolean;
  /** A wandering rare (ADR 0094): named and outlined in the rares' own jade. */
  rare?: boolean;
  /** A creature that only fights back (ADR 0058): named in a pale grey, not in red. */
  passive?: boolean;
  /** A townsperson's quest marker (ADR 0047); null for none, absent for anyone else. */
  questMarker?: QuestMarker | null;
  /** A creature a quest under way asks for (ADR 0047): a « ! » before its level. */
  hunted?: boolean;
}

/**
 * A townsperson dressed as a player (`outfit` of `data/mobs.ts`): the same
 * layers, levels and halo as a player's. Every piece at one level, so the
 * halo is that level too (the average of the pieces', `auraLevel`).
 */
function outfitOf(
  outfit: MobDefinition['outfit'],
): Pick<Entity, 'appearance' | 'upgrades' | 'aura' | 'rarities' | 'look' | 'mount'> {
  if (outfit === undefined) return {};
  const { level, look, mount, mountName: _name, ...appearance } = outfit;
  return {
    appearance,
    upgrades: {
      helmet: level,
      chest: level,
      weapon: level,
      offhand: level,
      gloves: level,
      boots: level,
    },
    aura: level,
    rarities: outfitRarities(appearance),
    look,
    mount: mount ?? '',
  };
}

/** A townsperson wears no cosmetic: the pieces drawn are the pieces worn. */
function outfitRarities(appearance: Record<string, string | undefined>): PieceRarities {
  const rarities: PieceRarities = {};
  for (const slot of AURA_SLOTS) {
    const id = appearance[slot];
    const rarity = id === undefined ? undefined : getItem(id)?.rarity;
    if (rarity !== undefined && rarity !== 'common') rarities[slot] = rarity;
  }
  return rarities;
}

declare global {
  interface Window {
    /** Set in development builds only; see `connect()`. */
    shefiraDebug?: { room: Room<ZoneSchema>; inventory?: InventoryState };
  }
}

/**
 * Name colour of an elite, the same as the contour of an item of its rarity
 * in the bag (`inventory.css`): a player reads one scale everywhere. A common
 * monster keeps the enemy red.
 */
const ELITE_NAME_COLOURS: Record<Exclude<Rarity, 'common'>, number> = {
  rare: 0x5aa9ff,
  epic: 0xc678ff,
  legendary: 0xffb347,
};

/** The button of the quests' dialogue that opens a keeper's own window (ADR 0047). */
const SERVICE_LABELS: Readonly<Record<string, string>> = {
  // The camp's tutor (ADR 0066): his quests first, his advice a button away.
  tutor: 'Écouter ses conseils',
  smith: 'Voir la forge',
  runesmith: 'Voir les runes',
  merchant: 'Voir la boutique',
  smelter: 'Voir la fonderie',
  alchemist: 'Voir les potions',
};

/** A zone boss's name (ADR 0024): a hotter red than the enemies' own. */
const BOSS_NAME_COLOUR = 0xff2a2a;
/**
 * A creature that waits to be struck (ADR 0058, SHE-69): a cool pale grey,
 * apart from the enemies' red, the townsfolk's straw, the green of those who
 * answer a click, every elite colour and the players' near-white.
 */
const PASSIVE_NAME_COLOUR = 0xaab4c0;

/** How fast a sprite slides toward the tile the server put it on. */
const INTERPOLATION_PER_MS = 0.012;
/**
 * Beyond this many tiles between the drawn and the real position, the
 * entity jumped (a journey through a portal, a respawn): it is put there at
 * once rather than seen gliding across the map.
 */
const SNAP_TILES = 4;
/** Dots move a tile at a time; four redraws a second keep up without costing a frame. */
const MINIMAP_REFRESH_MS = 250;

/** What the chat says when the server refuses an inventory action. */
const REFUSAL_MESSAGES: Record<InventoryRejected['reason'], string> = {
  'unknown-item': 'Objet introuvable.',
  'not-equipment': 'Cet objet ne s’équipe pas.',
  'level-too-low': 'Ton niveau est trop bas pour cet objet.',
  'wrong-class': 'Cet objet est réservé à une autre classe.',
  'bag-full': 'Ton sac est plein.',
  'not-usable': 'Cet objet ne s’utilise pas.',
  'full-health': 'Ta vie est déjà au maximum.',
  'bait-not-now': 'Aucune de tes quêtes ne demande cette créature pour l’instant.',
  'bait-here': 'Pose l’appât près d’un terrier, hors du village.',
  'bait-busy': 'Ta proie est déjà sortie : abats-la d’abord.',
  busy: 'Termine d’abord ton échange.',
  'not-destroyable': 'Une quête en cours te l’a confié : abandonne-la ou rends-la d’abord.',
  invalid: 'Action invalide.',
};

/** What the chat says when the server refuses a spell. */
const CAST_REFUSALS: Record<CastRejected['reason'], string> = {
  'unknown-spell': 'Sort inconnu.',
  'wrong-class': 'Ce sort n’est pas de ta classe.',
  cooldown: 'Ce sort n’est pas encore prêt.',
  energy: 'Pas assez d’énergie.',
  'no-target': 'Attaque d’abord un ennemi.',
  'out-of-range': 'Ta cible est trop loin.',
  dead: 'Tu es à terre.',
  mounted: 'Descends de ta monture pour combattre ({key}).',
  'not-learned': 'Tu ne connais pas encore ce sort.',
  'not-on-bar': 'Ce sort n’est pas dans ta barre.',
  'no-room': 'Pas de place pour charger.',
};

/** A refused spell in words; a touch screen has no mount key to name (ADR 0043). */
/** Where a character standing on a tile has its feet, as the scene draws them. */
function tileFeet(tile: { x: number; y: number }): { x: number; y: number } {
  return { x: tile.x * TILE_SIZE + TILE_SIZE / 2, y: tile.y * TILE_SIZE + TILE_SIZE };
}

/**
 * A fixed phrase the scene writes in the world with the bitmap font, in the
 * player's language. French keeps its accents here: the bitmap font has them,
 * so `uiText` (which strips them for the DOM font) only answers in English.
 * `{name}` placeholders are filled from `values`.
 */
function worldText(french: string, values: Readonly<Record<string, string | number>> = {}): string {
  const phrase = language() === 'en' ? uiText(french) : french;
  return phrase.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = values[key];
    return value === undefined ? whole : String(value);
  });
}

function castRefusal(reason: CastRejected['reason']): string {
  if (reason === 'mounted') {
    if (isTouch()) return 'Descends de ta monture pour combattre.';
    return uiFormat(CAST_REFUSALS.mounted, { key: actionKeyLabel('mount') });
  }
  return CAST_REFUSALS[reason];
}

/** Colyseus gives up reconnecting with this code (CloseCode.FAILED_TO_RECONNECT). */
const CLOSE_CODE_FAILED_TO_RECONNECT = 4003;

function leaveMessage(code: number): string {
  // A sanction or a flood (ADR 0037, ADR 0040) says why; nothing else does.
  const sanction = closeCodeText(code);
  if (sanction !== undefined) return sanction;
  switch (code) {
    case CLOSE_CODE_SESSION_REPLACED:
      return 'Session ouverte ailleurs';
    case CLOSE_CODE_FAILED_TO_RECONNECT:
      return 'Connexion perdue. Retour en jeu dès que possible…';
    case CLOSE_CODE_SERVER_RESTART:
      return 'Mise à jour du serveur. Retour en jeu dès qu’il revient…';
    default:
      return 'Déconnecté du serveur';
  }
}

/** A blow received buzzes this long, short enough to stay a tap on the shoulder. */
const VIBRATE_ON_HIT_MS = 30;

/** The guest's reminder waits for the quest's own toast to be read first. */
const GUEST_REMINDER_DELAY_MS = 2500;

/** A held finger starts dragging the walk after this long, then asks again this often. */
const HELD_WALK_DELAY_MS = 250;
const HELD_WALK_EVERY_MS = 150;

/** The socket's own codes for a server that went away (going away, abnormal closure). */
const CLOSE_CODE_GOING_AWAY = 1001;
const CLOSE_CODE_ABNORMAL = 1006;

/**
 * A leave that was nobody's decision: the network dropped for longer than
 * Colyseus waits (a phone locked in a pocket, a train tunnel, Wi-Fi to 4G).
 * A sanction, a session opened elsewhere or a player going home stays over.
 */
function rejoinable(code: number): boolean {
  return (
    code === CLOSE_CODE_SERVER_RESTART ||
    code === CLOSE_CODE_FAILED_TO_RECONNECT ||
    code === CLOSE_CODE_GOING_AWAY ||
    code === CLOSE_CODE_ABNORMAL
  );
}

/**
 * Back in the game once the page is in sight and online again (ADR 0043):
 * the character session is stored, so a reload joins where the server saved
 * the character. A mobile browser suspends a hidden page, so a reload now
 * would only happen when the player looks again anyway.
 */
function rejoinWhenBack(closedAt?: number): void {
  const attempt = (): void => {
    if (document.hidden || !navigator.onLine) return;
    document.removeEventListener('visibilitychange', attempt);
    window.removeEventListener('online', attempt);
    // Reloading onto a server still away would only show the login error.
    void waitForServer(`${serverHttpUrl()}/health`, closedAt).then(() => {
      window.location.reload();
    });
  };
  document.addEventListener('visibilitychange', attempt);
  window.addEventListener('online', attempt);
  attempt();
}

export class WorldScene extends Phaser.Scene {
  static readonly KEY = 'world';

  private session!: StoredSession;
  private manifest!: AssetManifest;
  private grid!: CollisionGrid;
  private room?: Room<ZoneSchema>;

  private readonly views = new Map<string, CharacterView>();
  /** Body atlases left out of the boot and already asked for, so a frame asks once. */
  private readonly requestedAtlases = new Set<string>();
  /** Shakes, hit stops and flashes of the big moments (ADR 0049). */
  private readonly juice = new Juice(this);
  private readonly dropViews = new Map<string, DropView>();
  /** Where each sprite is right now, in pixels, while it catches up to its tile. */
  private readonly rendered = new Map<string, { x: number; y: number }>();
  /** The local player's walk, predicted ahead of the server (ADR 0045); one per room. */
  private localMovement = new LocalMovement();
  /** The walking keys held (the arrows by default): a step asked per tile while one is down. */
  private readonly keyboardWalk = new KeyboardWalk();
  private status?: PixelText;
  /** The restart countdown on screen (`server_restart`), gone when the room is left. */
  private restartBanner?: RestartBanner;
  /** The white square on the tile the player last clicked to walk to. */
  private moveMarker?: MoveMarker;
  private footstepTrails?: FootstepTrails;
  /** The monster the yellow or red square sits under, followed until it falls. */
  private markedMobId?: string;
  /** Set by `zone_change`: the room is closing because the player walked out. */
  private nextZone?: StoredSession;
  private chat?: ChatPanel;
  private readonly refusals = new RefusalBanner();
  private readonly placeBanner = new RefusalBanner('place');
  private social?: SocialClient;
  /** Another player's gear, opened from the menu a click on them shows (ADR 0085). */
  private inspectPanel?: InspectPanel;
  /** Friends in this zone (ADR 0033), as the server last placed them: green on both maps. */
  private friendPositions: FriendPositions['positions'] = [];
  /** The zone's players as `admin_players` last listed them (admins only). */
  private adminPlayers: AdminPlayers['players'] = [];
  /** The den where a quest's bait is to be laid (ADR 0047): a ring and an arrow. */
  private denGuide?: DenGuide;
  /** Leads to the camp or the place a quest under way asks for (ADR 0047). */
  private questPointer?: TutorialPointer;
  private bossDefeatTip?: BossDefeatTip;
  private spellBar?: SpellBar;
  /** A health potion drunk without the bag: in the spell bar, or under the thumb. */
  private quickPotion?: QuickPotion;
  /** The item icons for the DOM, once loaded (null when the atlas failed). */
  private itemIcons: CssAtlas | null = null;
  /** The grimoire (ADR 0048): every spell of the class, and the four on the bar. */
  private spellbook?: SpellbookPanel;
  /** The touch screen's HUD (ADR 0043): attack and mount buttons, the drawer. */
  private touchHud?: TouchHud;
  /**
   * A finger (or a mouse button) held on the ground after asking for a walk
   * there: the walk follows it (ADR 0043). The last tile asked, to ask again
   * only when it changes.
   */
  private heldWalk?: { since: number; tile: { x: number; y: number } };
  private playerPress?: {
    name: string;
    from: { x: number; y: number };
    timer: Phaser.Time.TimerEvent;
  };
  /** The mount being summoned, as the server said (scene time); null otherwise. */
  private mountCast: { startedAt: number; durationMs: number } | null = null;
  private minimap?: Minimap;
  private worldMap?: WorldMap;
  /** The portals' window (ADR 0017), and what the server last said of the player's journeys. */
  private travelPanel?: TravelPanel;
  private travel: TravelState = { discovered: [], respawn: null };
  /** Whether the saved journeys arrived: only later changes are announced. */
  private travelKnown = false;
  /** The wall of blue flames a journey plays behind (ADR 0017), and its safety net. */
  private veil?: TeleportVeil;
  /** Shefira's scenes (ADR 0067), played for this player alone when the server says. */
  private cinematics?: CinematicPlayer;
  private veilTimer?: Phaser.Time.TimerEvent;
  /** The village's name board, shown on walking in or landing at its portal. */
  private villageSign?: VillageSign;
  private combatFrame?: CombatFrame;
  private lowHealth?: LowHealthAlert;
  /** The night over the open world (ADR 0093); none in the camp, the caves or the dungeons. */
  private nightShade?: NightShade;
  private weather?: WeatherLayer;
  private pingMeter?: PingMeter;
  /** Zone bosses (ADR 0024): the big bar at the top, and their discs on the ground. */
  private bossBar?: BossBar;
  private targetFrame?: TargetFrame;
  /** The boss the boss bar shows, if it is up: its states stay in sight under the bar. */
  private barBossId?: string;
  /** The dungeon's boss lying dead (ADR 0090): which, and when it stands up, by `Date.now()`. */
  private bossWaking?: { bossId: string; at: number };
  private bossEffects?: BossEffects;
  /** Shefira's Aire (ADR 0068): her fight in the Aire, the crevasse's window in the world. */
  private aerie?: AerieClient;
  /** `Date.now()` the player is out of combat, as the server last said (`combat_lock`). */
  private combatUntil = 0;
  /** The portal walked up to: it is used, and its window opens, once the player reaches it. */
  private visitingPortal?: ZonePortal;
  private visitingAuction?: { prop: string; x: number; y: number };
  private auctionRequestId = 0;
  private readonly auctionRequests = new Map<
    number,
    {
      resolve: (value: { ticket: string; biome: BiomeId }) => void;
      reject: (reason: Error) => void;
      timer: number;
    }
  >();
  /** Chests of the zone (ADR 0021), and the one being walked to. */
  private chestLayer?: ChestLayer;
  /** The zone's prop images, for the world editor (ADR 0022). */
  private propImages: (PropImage | undefined)[] = [];
  /** The farm the dragon burns, standing until the player's story burns it (ADR 0067). */
  private beforeTheFire?: BeforeTheFire;
  /** The world editor, for an admin in the open world (ADR 0022). */
  private editor?: WorldEditor;
  private visitingChest?: ChestPlacement;
  /** When the minimap last redrew its dots; a few times a second is plenty. */
  private minimapAt = 0;
  /** The zone being drawn, for the minimap's sense of place. */
  private zone?: ZoneDefinition;
  /** Where the player was last told they stand: a village or a biome name. */
  private place = '';
  private inventoryPanel?: InventoryPanel;
  private profilePanel?: ProfilePanel;
  private achievementToast?: AchievementToast;
  /** The quests' journal, dialogue, tracker and notices (ADR 0047). */
  private questClient?: QuestClient;
  /** The world events (ADR 0050): the tracker, the ranking, and where the battle is. */
  private worldEvents?: WorldEventClient;
  /** The road encounters (ADR 0093): their marks, the lost chests, the peddler's window. */
  private encounters?: EncounterClient;
  /** The villages' wanted boards (ADR 0096): the board's window, the hunted beast's place. */
  private bounties?: BountyClient;
  /** The lost chest walked to, opened on arrival within reach. */
  private visitingRoadChest?: string;
  private questTargetCache?: { stamp: string; targets: readonly QuestTarget[] };
  private questMarkerCache?: { stamp: number; markers: readonly MinimapMarker[] };
  /** The smith's window (ADR 0013), and the smith it was opened at. */
  private smithPanel?: SmithPanel;
  private smithId?: string;
  /** The runesmith's window (ADR 0057), and the runesmith it was opened at. */
  private runesmithPanel?: RunesmithPanel;
  private runesmithId?: string;
  /** The merchant's window (ADR 0016), and the merchant it was opened at. */
  private merchantPanel?: MerchantPanel;
  /** The account's chest (ADR 0079), at every village's. */
  private stashPanel?: StashPanel;
  private stashId?: string;
  private shopPanel?: ShopPanel;
  private auctionPanel?: AuctionPanel;
  private ladderPanel?: LadderPanel;
  private bestiaryPanel?: BestiaryPanel;
  private merchantId?: string;
  /** The smelter's window (ADR 0018), and the smelter it was opened at. */
  private fusionPanel?: FusionPanel;
  /** The alchemist's window (ADR 0026), and the alchemist it was opened at. */
  private alchemyPanel?: AlchemyPanel;
  private alchemistId?: string;
  /** The game master's window (ADR 0025), for an admin only; F3 opens it. */
  private adminPanel?: AdminPanel;
  /** The server said this account is an admin (ADR 0025). */
  private admin = false;
  private smelterId?: string;
  private tutorPanel?: TutorPanel;
  /** The camp's next step and what to click for it (ADR 0078); only in the camp. */
  private campCoach?: CampCoach;
  private tutorId?: string;
  /** The smith walked up to: his window opens once the player stands beside him. */
  private visitingNpc?: { id: string; front: { x: number; y: number } };
  private onlineCount?: OnlineCount;
  /** The local player's own progression; nobody else's is ever sent. */
  private progress?: ProgressEvent;
  /** The last inventory the server sent, kept so a panel opened later starts full. */
  private inventory?: InventoryState;
  /** Each entity's last animation: a change to `attack` or `death` is heard. */
  private readonly lastAnims = new Map<string, string>();
  /** Drops already there on arrival fall silently; later ones are heard landing. */
  private dropsPrimed = false;
  /** Whether a template on the ground beats what is worn, by template; forgotten when the bag or the level changes. */
  private readonly betterDrops = new Map<string, boolean>();

  constructor() {
    super(WorldScene.KEY);
  }

  init(data: WorldSceneData): void {
    this.manifest = data.manifest;
    this.session = data.session;
    // A zone change restarts this same scene instance: whatever the last zone
    // left behind was destroyed with its display list and must be forgotten.
    this.views.clear();
    this.dropViews.clear();
    this.rendered.clear();
    this.lastAnims.clear();
    this.dropsPrimed = false;
    this.status = undefined;
    this.moveMarker = undefined;
    this.markedMobId = undefined;
    this.visitingNpc = undefined;
    this.room = undefined;
    this.nextZone = undefined;
  }

  create(): void {
    let zone = getZone(this.session.character.zoneId);
    if (zone === undefined) {
      // A zone that no longer exists (the meadow and the forest merged into
      // the world): the server relocates the character at join, and the
      // session follows so a reload lands in the right place.
      zone = getZone(DEFAULT_ZONE_ID);
      if (zone === undefined) {
        hideLoader();
        this.showStatus(
          worldText('Zone « {zone} » inconnue', { zone: this.session.character.zoneId }),
        );
        return;
      }
      this.session = {
        ...this.session,
        character: { ...this.session.character, zoneId: zone.id },
      };
      saveSession(this.session);
    }
    this.grid = buildCollisionGrid(zone);
    this.zone = zone;
    this.place = '';

    this.registerAnimations();
    drawTerrain({ scene: this, zone, manifest: this.manifest });
    this.propImages = drawProps({ scene: this, zone, manifest: this.manifest });
    this.beforeTheFire = new BeforeTheFire(this, zone, this.manifest, this.propImages);
    this.chestLayer = new ChestLayer(this, zone, this.manifest);
    this.moveMarker = new MoveMarker(this);
    this.footstepTrails = new FootstepTrails(this, (key) => this.manifest.sheets[key]);
    this.denGuide?.destroy();
    this.denGuide = new DenGuide(this);
    this.questPointer?.destroy();
    this.questPointer = new TutorialPointer(this);
    this.minimap?.destroy();
    this.minimap = new Minimap({ zone, grid: this.grid, field: terrainFieldOf(zone) });
    this.worldMap?.destroy();
    // A new map hears of the quests' targets at the next minimap refresh.
    this.questTargetCache = undefined;
    this.worldMap = new WorldMap({ zone, picture: this.minimap.base });
    this.adminPlayers = [];
    this.followStory();
    this.armMapTeleport();
    if (this.progress !== undefined) this.worldMap.setLevel(this.progress.level);
    this.travelPanel?.destroy();
    this.travelPanel = new TravelPanel({
      zone,
      onTeleport: (portalId) => {
        this.travelTo(portalId);
      },
      onSetRespawn: (portalId) => {
        this.room?.send('set_respawn', { portalId });
      },
    });
    this.travelPanel.setTravel(this.travel);
    this.veil?.destroy();
    this.veil = new TeleportVeil(this, this.manifest.sheets[TELEPORT_FLAME_SHEET]);
    this.cinematics?.destroy();
    this.cinematics = new CinematicPlayer(this, this.manifest, (kind) => {
      this.juice.jolt(kind);
    });
    this.villageSign?.destroy();
    this.villageSign = new VillageSign();
    this.combatFrame?.destroy();
    this.combatFrame = new CombatFrame();
    this.lowHealth?.destroy();
    this.lowHealth = new LowHealthAlert();
    this.nightShade?.destroy();
    this.nightShade =
      zone.id === DEFAULT_ZONE_ID
        ? new NightShade(this, zone.width * TILE_SIZE, zone.height * TILE_SIZE)
        : undefined;
    this.weather?.destroy();
    this.weather = zone.id === DEFAULT_ZONE_ID ? new WeatherLayer(this) : undefined;
    this.pingMeter?.destroy();
    this.pingMeter = new PingMeter((answer) => {
      this.room?.ping(answer);
    });
    this.bossBar?.destroy();
    this.bossBar = new BossBar();
    this.bossWaking = undefined;
    this.targetFrame?.destroy();
    this.targetFrame = new TargetFrame();
    this.bossEffects?.destroy();
    this.bossEffects = new BossEffects(this);
    this.travelPanel.setCombatUntil(this.combatUntil);
    if (this.inventory !== undefined) this.travelPanel.setGold(this.inventory.gold);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.playerPress?.timer.remove();
      this.playerPress = undefined;
      this.tutorPanel?.destroy();
      this.tutorPanel = undefined;
      this.restartBanner?.destroy();
      this.restartBanner = undefined;
      this.campCoach?.destroy();
      this.campCoach = undefined;
      this.minimap?.destroy();
      this.minimap = undefined;
      this.worldMap?.destroy();
      this.worldMap = undefined;
      this.travelPanel?.destroy();
      this.travelPanel = undefined;
      this.veil?.destroy();
      this.veil = undefined;
      this.cinematics?.destroy();
      this.cinematics = undefined;
      this.villageSign?.destroy();
      this.villageSign = undefined;
      this.combatFrame?.destroy();
      this.combatFrame = undefined;
      this.lowHealth?.destroy();
      this.lowHealth = undefined;
      this.nightShade?.destroy();
      this.nightShade = undefined;
      this.weather?.destroy();
      this.weather = undefined;
      this.pingMeter?.destroy();
      this.pingMeter = undefined;
      this.bossBar?.destroy();
      this.bossBar = undefined;
      this.targetFrame?.destroy();
      this.targetFrame = undefined;
      this.bossEffects?.destroy();
      this.bossEffects = undefined;
      this.aerie?.destroy();
      this.aerie = undefined;
      this.editor?.destroy();
      this.editor = undefined;
    });

    this.fitCameraBounds();
    // Outside a small map: grass around the open air, the palette's dark under
    // ground (dungeons, caves, the Aire), where green framed the walls.
    const outdoors = zone.id === DEFAULT_ZONE_ID || zone.id === TUTORIAL_ZONE_ID;
    this.cameras.main.setBackgroundColor(outdoors ? '#1b2b12' : '#0d0a0a');
    this.cameras.main.roundPixels = true;

    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      // While the world editor is open, a click edits: the player stays put.
      if (this.editor?.active === true) return;
      // The side buttons are keys (`settings/mouse-buttons.ts`): pressing one never walks.
      if (mouseButtonCode(pointer.button) !== undefined) return;
      const other = this.playerUnder(pointer);
      if (!pointer.wasTouch) {
        const press = mousePressOf(pointer.button, other !== undefined);
        if (press === 'ignore') return;
        if (press === 'social' && other !== undefined) {
          const at = this.clientPointOf(pointer);
          this.social?.openPlayerMenu(other, at.x, at.y);
          return;
        }
      } else if (other !== undefined) {
        // A finger on another player: a tap still plays, held it asks for the menu.
        this.armPlayerPress(pointer, other);
        return;
      }
      const walk = this.requestMove(pointer);
      this.heldWalk = walk === undefined ? undefined : { since: this.time.now, tile: walk };
    });
    const letGo = (): void => {
      this.heldWalk = undefined;
    };
    this.input.on(Phaser.Input.Events.POINTER_UP, (pointer: Phaser.Input.Pointer) => {
      this.releasePlayerPress(pointer, false);
      letGo();
    });
    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, () => {
      this.playerPress?.timer.remove();
      this.playerPress = undefined;
      letGo();
    });
    // The hand cursor tells a townsperson who answers (the smith, the
    // merchant: `interactive` in the mob data) from the passers-by, and a drop that a click picks up.
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (this.playerPress !== undefined && pointer.isDown) {
        const at = this.clientPointOf(pointer);
        const from = this.playerPress.from;
        if (Math.hypot(at.x - from.x, at.y - from.y) > PLAYER_PRESS_SLOP_PX) {
          this.releasePlayerPress(pointer, true);
        }
      }
      if (this.editor?.active === true) return;
      const answers =
        this.dropAt(this.tileUnder(pointer)) !== undefined ||
        this.npcUnder(pointer) !== undefined ||
        this.aerie?.answers(this.tileUnder(pointer), { x: pointer.worldX, y: pointer.worldY }) ===
          true ||
        this.portalAt(this.tileUnder(pointer)) !== undefined ||
        (this.zone !== undefined &&
          (auctionHouseAt(this.zone, this.tileUnder(pointer)) !== undefined ||
            exitBuildingAt(this.zone, this.tileUnder(pointer)) !== undefined)) ||
        this.chestLayer?.chestAt(this.tileUnder(pointer)) !== undefined ||
        this.encounters?.chestAt(this.tileUnder(pointer)) !== undefined;
      this.input.setDefaultCursor(answers ? 'var(--cursor-pointer)' : 'var(--cursor-default)');
    });
    this.scale.on(Phaser.Scale.Events.RESIZE, () => {
      this.fitCameraBounds();
      this.placeStatus();
    });
    // On a phone, the map's last rows scroll above the spell bar and the chat (SHE-79).
    const stopWatchingHud = onBottomHudResize(() => {
      this.fitCameraBounds();
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stopWatchingHud);

    void this.connect();
  }

  /**
   * One animation per layer, animation and direction, built from the atlas
   * frames. Every layer of a character shares the frame count, so they stay in
   * lockstep without any cross-layer bookkeeping.
   */
  private registerAnimations(): void {
    for (const [key, sheet] of Object.entries(this.manifest.sheets)) {
      // Bodies, the looping effects drawn with them (the +10 halo), and the
      // animated props (the portal's vortex).
      const isProp = key.startsWith('prop.');
      if (!key.startsWith('body.') && !key.startsWith('effect.') && !isProp) continue;
      const texture = this.textures.get(sheet.atlas);
      const frames = texture.getFrameNames();

      const clips = new Map<string, string[]>();
      for (const frame of frames) {
        if (!frame.startsWith(`${sheet.prefix}/`)) continue;
        const clip = frame.slice(0, frame.lastIndexOf('/'));
        const list = clips.get(clip) ?? [];
        list.push(frame);
        clips.set(clip, list);
      }

      for (const [clip, list] of clips) {
        if (this.anims.exists(clip)) continue;
        const ordered = list.sort((a, b) => {
          const indexA = Number.parseInt(a.slice(a.lastIndexOf('/') + 1), 10);
          const indexB = Number.parseInt(b.slice(b.lastIndexOf('/') + 1), 10);
          return indexA - indexB;
        });
        // Riding poses loop like the ones they stand for: `ride` is the idle
        // on a mount, `ride-walk` its walk.
        const isWalk = clip.includes('/walk/') || clip.includes('/ride-walk/');
        const isIdle = clip.includes('/idle/') || clip.includes('/ride/');
        // Attack, hurt and death play once and hold; idle and walk loop, and
        // so does an animated prop (its clip is `prop.<name>/idle`, no direction).
        this.anims.create({
          key: clip,
          frames: ordered.map((frame) => ({ key: sheet.atlas, frame })),
          // A prop's loop is magic or fire, livelier than a body at rest.
          frameRate: isWalk ? 7 : isProp ? 8 : isIdle ? 3 : 10,
          repeat: isWalk || isIdle || isProp ? -1 : 0,
        });
      }
    }
  }

  private async connect(): Promise<void> {
    this.showStatus(worldText('Connexion…'));
    try {
      const { instanceId } = this.session.character;
      const room = await new Client(serverWsUrl()).joinOrCreate<ZoneSchema>('zone', {
        token: this.session.token,
        zoneId: this.session.character.zoneId,
        // An instance (ADR 0068) is joined by the id the server gave at its gate.
        ...(instanceId === undefined ? {} : { instanceId }),
      });
      this.room = room;
      this.localMovement = new LocalMovement();
      this.aerie?.destroy();
      this.aerie = this.zone === undefined ? undefined : this.openAerie(this.zone);
      this.hideStatus();
      // In the world: the loading screen, up since the page opened, lifts.
      hideLoader();
      // Dev only: poke at the live room from the browser console.
      if (import.meta.env.DEV) window.shefiraDebug = { room: this.room };

      room.onMessage('move_rejected', (payload: unknown) => {
        // The server refused the path: the walk already started goes back to
        // where it says the player stands.
        this.hideMarker();
        const parsed = MoveRejectedEventSchema.safeParse(payload);
        if (parsed.success) this.localMovement.reject(parsed.data, this.time.now);
      });
      room.onMessage('inventory', (payload: unknown) => {
        const parsed = InventoryStateSchema.safeParse(payload);
        if (!parsed.success) return;
        const sound =
          this.inventory === undefined ? undefined : inventoryCue(this.inventory, parsed.data);
        if (sound !== undefined) cue(sound);
        this.inventory = parsed.data;
        this.betterDrops.clear();
        this.refreshTouchMount();
        this.quickPotion?.setInventory(parsed.data);
        this.tutorPanel?.refresh();
        this.inventoryPanel?.setInventory(parsed.data);
        this.smithPanel?.setInventory(parsed.data);
        this.runesmithPanel?.setInventory(parsed.data);
        this.fusionPanel?.setInventory(parsed.data);
        this.alchemyPanel?.setInventory(parsed.data);
        this.adminPanel?.setInventory(parsed.data);
        this.merchantPanel?.setInventory(parsed.data);
        this.encounters?.setInventory(parsed.data);
        this.stashPanel?.setInventory(parsed.data);
        this.shopPanel?.setInventory(parsed.data);
        this.auctionPanel?.setInventory(parsed.data);
        this.travelPanel?.setGold(parsed.data.gold);
        this.profilePanel?.setEquipped(parsed.data.equipped);
        this.profilePanel?.setGold(parsed.data.gold);
        if (import.meta.env.DEV && window.shefiraDebug !== undefined) {
          window.shefiraDebug.inventory = parsed.data;
        }
      });
      room.onMessage('profile', (payload: unknown) => {
        const parsed = ProfileEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.profilePanel?.setProfile(parsed.data);
        this.bestiaryPanel?.setProfile(parsed.data);
      });
      // Achievements and titles (ADR 0046).
      room.onMessage('achievements', (payload: unknown) => {
        const parsed = AchievementsEventSchema.safeParse(payload);
        if (parsed.success) this.profilePanel?.setAchievements(parsed.data);
      });
      room.onMessage('achievement_unlocked', (payload: unknown) => {
        const parsed = AchievementUnlockedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.level-up', 0.8);
        this.achievementToast?.show(parsed.data.ids);
        for (const id of parsed.data.ids) {
          const achievement = getAchievement(id);
          if (achievement !== undefined) this.chat?.notice(achievementNotice(achievement));
        }
        // The window may be open on the list: it follows.
        this.room?.send('achievements', {});
      });
      // Asked once on arrival: a reward waiting to be claimed shows on the HUD button.
      room.send('achievements', {});
      // Quests (ADR 0047): the journal, a quest turned in, a refusal.
      room.onMessage('quests', (payload: unknown) => {
        this.questClient?.onQuests(payload);
        this.followStory();
      });
      room.onMessage('quest_completed', (payload: unknown) => {
        this.questClient?.onCompleted(payload);
        // The meadow behind them: a guest is asked to keep the character (after the toast).
        const parsed = QuestCompletedSchema.safeParse(payload);
        if (parsed.success && parsed.data.questId === REGISTER_REMINDER_QUEST) {
          this.time.delayedCall(GUEST_REMINDER_DELAY_MS, () => {
            void remindGuestToRegister(this.session.token);
          });
        }
      });
      // Shefira shows herself (ADR 0067): input held until the scene is over.
      room.onMessage('cinematic', (payload: unknown) => {
        const parsed = CinematicEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.localMovement.stop();
        this.questClient?.closeDialog();
        // The camera sits on the player (whole pixels): the scene is laid around them.
        const play = (): void => {
          const { midPoint } = this.cameras.main;
          void this.cinematics?.play(parsed.data.id, { x: midPoint.x, y: midPoint.y });
        };
        // In the Aire, her flight waits for her to take the sword and rise (ADR 0068).
        const delay = this.aerie?.cinematicDelay() ?? 0;
        if (delay > 0) this.time.delayedCall(delay, play);
        else play();
      });
      room.onMessage('quest_rejected', (payload: unknown) => {
        this.questClient?.onRejected(payload);
      });
      // Contracts (ADR 0051): the day's and the week's, one cashed, a refusal.
      room.onMessage('contracts', (payload: unknown) => {
        this.questClient?.onContracts(payload);
      });
      room.onMessage('contract_claimed', (payload: unknown) => {
        this.questClient?.onContractClaimed(payload);
      });
      room.onMessage('contract_rejected', (payload: unknown) => {
        this.questClient?.onContractRejected(payload);
      });
      room.onMessage('streak_reward', (payload: unknown) => {
        this.questClient?.onStreakReward(payload);
      });
      room.onMessage('boss_bonus', (payload: unknown) => {
        this.questClient?.onBossBonus(payload);
      });
      room.onMessage('bait_laid', (payload: unknown) => {
        this.questClient?.onBaitLaid(payload);
      });
      room.onMessage('achievement_rejected', (payload: unknown) => {
        const parsed = AchievementRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.refusals.show(ACHIEVEMENT_REFUSAL_TEXT[parsed.data.reason]);
      });
      room.onMessage('fuse_result', (payload: unknown) => {
        const parsed = FuseResultSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.fuse');
        this.fusionPanel?.showResult(parsed.data);
      });
      room.onMessage('fuse_rejected', (payload: unknown) => {
        const parsed = FuseRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.fusionPanel?.showRejected(parsed.data);
      });
      room.onMessage('brew_result', (payload: unknown) => {
        const parsed = BrewResultSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.brew');
        this.alchemyPanel?.showResult(parsed.data);
      });
      room.onMessage('brew_rejected', (payload: unknown) => {
        const parsed = BrewRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.alchemyPanel?.showRejected(parsed.data);
      });
      room.onMessage('travel', (payload: unknown) => {
        const parsed = TravelStateSchema.safeParse(payload);
        if (!parsed.success) return;
        const fresh = parsed.data.discovered.filter((id) => !this.travel.discovered.includes(id));
        const chosen = parsed.data.respawn !== this.travel.respawn ? parsed.data.respawn : null;
        // The first message is the saved state, not news.
        const news = this.travelKnown;
        this.travel = parsed.data;
        this.travelKnown = true;
        this.travelPanel?.setTravel(parsed.data);
        this.profilePanel?.setPortalsFound(parsed.data.discovered.length);
        if (!news || this.zone === undefined) return;
        if (fresh.length > 0) cue('sfx.portal');
        for (const id of fresh) {
          const name = portalById(this.zone, id)?.name ?? id;
          this.chat?.notice(
            uiFormat('Portail de {name} activé : tu peux y revenir de partout.', { name }),
          );
        }
        if (chosen !== null) {
          const name = portalById(this.zone, chosen)?.name ?? chosen;
          this.chat?.notice(uiFormat('Tu te relèveras désormais à {name}.', { name }));
        }
      });
      room.onMessage('teleported', (payload: unknown) => {
        const parsed = TeleportedSchema.safeParse(payload);
        if (!parsed.success) return;
        this.travelPanel?.showTeleported();
        this.visitingNpc = undefined;
        this.visitingPortal = undefined;
        this.visitingAuction = undefined;
        this.auctionPanel?.close();
        this.hideMarker();
        const name =
          (this.zone === undefined
            ? undefined
            : portalById(this.zone, parsed.data.portalId)?.name) ?? parsed.data.portalId;
        this.chat?.notice(
          uiFormat('Te voici à {name} ({cost} or).', { name, cost: parsed.data.cost }),
        );
        // The message can outrun the patch that moves the player: the flames
        // hold a moment, so the new place is drawn before they part.
        this.clearVeil(250);
      });
      room.onMessage('auction_access', (payload: unknown) => {
        const parsed = AuctionAccessEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const pending = this.auctionRequests.get(parsed.data.requestId);
        if (pending === undefined) return;
        window.clearTimeout(pending.timer);
        this.auctionRequests.delete(parsed.data.requestId);
        if (parsed.data.ok)
          pending.resolve({ ticket: parsed.data.ticket, biome: parsed.data.biome });
        else pending.reject(new Error('Approche-toi de la porte de l’hôtel des ventes.'));
      });
      room.onMessage('admin', (payload: unknown) => {
        const parsed = AdminEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.admin = true;
        this.openAdminPanel();
        this.armMapTeleport();
        if (this.zone?.id !== DEFAULT_ZONE_ID || this.editor !== undefined) return;
        this.editor = new WorldEditor({
          scene: this,
          manifest: this.manifest,
          props: this.propImages,
          chests: this.chestLayer,
          mobAt: (tile) => this.inhabitantAt(tile),
          token: this.session.token,
          canSave: parsed.data.editorSaves,
        });
        this.chat?.notice(
          uiText(
            'Admin : F2 ouvre l’éditeur de carte, F3 le panneau MJ, un clic sur la carte (M) t’y téléporte.',
          ),
        );
      });
      room.onMessage('admin_players', (payload: unknown) => {
        const parsed = AdminPlayersSchema.safeParse(payload);
        if (!parsed.success) return;
        this.adminPlayers = parsed.data.players;
        this.adminPanel?.setPlayers(parsed.data.players);
      });
      room.onMessage('admin_result', (payload: unknown) => {
        const parsed = AdminResultSchema.safeParse(payload);
        if (!parsed.success) return;
        this.adminPanel?.showResult(parsed.data);
        // `/event` in the chat (ADR 0050) is answered in the chat.
        if (parsed.data.action === 'start_event' || parsed.data.action === 'stop_event') {
          this.chat?.notice(
            uiText(parsed.data.action === 'start_event' ? 'Événement lancé.' : 'Événement arrêté.'),
          );
        }
      });
      room.onMessage('admin_rejected', (payload: unknown) => {
        const parsed = AdminRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        this.adminPanel?.showRejected(parsed.data);
        if (parsed.data.reason === 'event-busy') {
          this.chat?.notice(uiText('Un événement est déjà en cours.'));
        } else if (parsed.data.reason === 'no-event') {
          this.chat?.notice(uiText('Aucun événement ici.'));
        }
      });
      room.onMessage('chests', (payload: unknown) => {
        const parsed = ChestsEventSchema.safeParse(payload);
        if (parsed.success) this.chestLayer?.apply(parsed.data.chests);
      });
      room.onMessage('chest_opened', (payload: unknown) => {
        const parsed = ChestOpenedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.discover');
        const found = parsed.data.items
          .map(
            (item) =>
              `${getItem(item.templateId)?.name ?? item.templateId}${item.quantity > 1 ? ` ×${String(item.quantity)}` : ''}`,
          )
          .join(', ');
        this.chat?.notice(
          uiFormat("Le coffre s'ouvre et laisse tomber : {items}.", { items: found }),
        );
      });
      room.onMessage('chest_rejected', (payload: unknown) => {
        const parsed = ChestRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        const why: Record<string, string> = {
          locked:
            'Le coffre est enchaîné : il faut d’abord nettoyer le camp avec ceux qui le gardent.',
          opened: 'Ce coffre est vide pour l’instant.',
          'too-far': 'Approche-toi du coffre.',
          dead: 'Les morts n’ouvrent pas de coffre.',
        };
        const line = why[parsed.data.reason];
        if (line !== undefined) this.refusals.show(line);
      });
      room.onMessage('travel_rejected', (payload: unknown) => {
        const parsed = TravelRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.clearVeil(0);
        if (parsed.data.seconds !== undefined) this.setCombatFor(parsed.data.seconds * 1000);
        this.travelPanel?.open();
        this.travelPanel?.showRejected(parsed.data);
      });
      room.onMessage('upgrade_result', (payload: unknown) => {
        const parsed = UpgradeResultSchema.safeParse(payload);
        if (parsed.success) this.smithPanel?.showResult(parsed.data);
      });
      room.onMessage('sell_result', (payload: unknown) => {
        const parsed = SellResultSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.coins');
        this.merchantPanel?.showSold(parsed.data);
      });
      room.onMessage('sell_rejected', (payload: unknown) => {
        const parsed = SellRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.merchantPanel?.showSellRejected(parsed.data);
      });
      room.onMessage('stash', (payload: unknown) => {
        const parsed = StashStateSchema.safeParse(payload);
        if (parsed.success) this.stashPanel?.setStash(parsed.data);
      });
      room.onMessage('stash_result', (payload: unknown) => {
        const parsed = StashResultSchema.safeParse(payload);
        if (!parsed.success) return;
        cue(parsed.data.direction === 'deposit' ? 'sfx.drop' : 'sfx.pickup');
        this.stashPanel?.showMoved(parsed.data);
      });
      room.onMessage('stash_rejected', (payload: unknown) => {
        const parsed = StashRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.stashPanel?.showRejected(parsed.data);
      });
      room.onMessage('buy_result', (payload: unknown) => {
        const parsed = BuyResultSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.coins');
        this.merchantPanel?.showBought(parsed.data);
      });
      room.onMessage('buy_rejected', (payload: unknown) => {
        const parsed = BuyRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.merchantPanel?.showBuyRejected(parsed.data);
      });
      room.onMessage('reforge_result', (payload: unknown) => {
        const parsed = ReforgeResultSchema.safeParse(payload);
        if (parsed.success) this.runesmithPanel?.showResult(parsed.data);
      });
      room.onMessage('reforge_rejected', (payload: unknown) => {
        const parsed = ReforgeRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.runesmithPanel?.showRejected(parsed.data);
      });
      room.onMessage('upgrade_rejected', (payload: unknown) => {
        const parsed = UpgradeRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.smithPanel?.showRejected(parsed.data);
      });
      room.onMessage('inventory_rejected', (payload: unknown) => {
        const parsed = InventoryRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.refusals.show(REFUSAL_MESSAGES[parsed.data.reason]);
      });
      room.onMessage('progress', (payload: unknown) => {
        const parsed = ProgressEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const previous = this.progress;
        this.progress = parsed.data;
        this.betterDrops.clear();
        this.inventoryPanel?.setProgress(parsed.data);
        this.profilePanel?.setProgress(parsed.data);
        this.spellBar?.setProgress(parsed.data);
        this.spellbook?.setLevel(parsed.data.level);
        this.worldMap?.setLevel(parsed.data.level);
        this.adminPanel?.setLevel(parsed.data.level);
        this.questClient?.setLevel(parsed.data.level);
        const gained = parsed.data.gained ?? 0;
        if (gained > 0) this.floatText(room.sessionId, `+${String(gained)} XP`, 0x9d7cd8, 900);
        // Levelling is worth a line in the chat; ordinary kills are not.
        if (previous !== undefined && parsed.data.level > previous.level) {
          this.chat?.notice(
            uiFormat('Tu passes au niveau {level} !', { level: parsed.data.level }),
          );
        }
      });
      room.onMessage('level_up', (payload: unknown) => {
        const parsed = LevelUpEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const view = this.views.get(parsed.data.id);
        if (view === undefined) return;
        this.cueOn('sfx.level-up', parsed.data.id);
        view.flash();
        const id = parsed.data.id;
        if (id === room.sessionId) this.juice.levelUp();
        playLevelUpBurst(this, () => this.rendered.get(id));
        this.floatText(
          id,
          worldText('NIVEAU {level}', { level: parsed.data.level }),
          0xf0c050,
          1600,
        );
      });
      room.onMessage('hit', (payload: unknown) => {
        const parsed = HitEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const key = parsed.data.target === 'mob' ? `mob:${parsed.data.id}` : parsed.data.id;
        const view = this.views.get(key);
        if (view === undefined) return;
        const { amount, crit = false, by } = parsed.data;
        // The player's own wound is felt, not heard from afar.
        if (key === room.sessionId) {
          cue('sfx.hurt');
          // On a phone the blow is felt too (ADR 0043); Android only, iOS has no vibrate.
          if (preference('vibration') && isTouch() && 'vibrate' in navigator)
            navigator.vibrate(VIBRATE_ON_HIT_MS);
          const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
          if (me !== undefined && isHeavyBlow(amount, me.maxHp)) this.juice.jolt('heavy');
        } else this.cueOn(crit ? 'sfx.crit' : 'sfx.hit', key);
        view.flash();
        // One's own crit lands with weight: both hold their frame and the screen jolts.
        if (crit && by === room.sessionId) {
          this.juice.hitStop([view, this.views.get(room.sessionId)]);
          this.juice.jolt('crit');
        }
        this.floatDamage(key, amount, crit);
      });

      // A broken wire is not a goodbye: the SDK retries with backoff while the
      // server keeps the character standing where it was. Only when it gives
      // up, or when the server ended the session on purpose, is it over.
      room.onDrop(() => {
        this.showStatus(worldText('Connexion perdue. Reconnexion…'));
      });
      room.onReconnect(() => {
        this.hideStatus();
      });
      room.onMessage('zone_change', (payload: unknown) => {
        const parsed = ZoneChangeEventSchema.safeParse(payload);
        if (!parsed.success) return;
        // The server already saved the character there; the stored session
        // follows, so a reload lands in the same place.
        // A zone that is no instance forgets the instance left behind.
        const { instanceId: _left, ...character } = this.session.character;
        this.nextZone = {
          ...this.session,
          character: { ...character, ...parsed.data },
        };
        saveSession(this.nextZone);
      });
      room.onLeave((code) => {
        if (code === CLOSE_CODE_ZONE_CHANGE && this.nextZone !== undefined) {
          const session = this.nextZone;
          this.chat?.destroy({ carry: true });
          this.chat = undefined;
          this.scene.restart({ manifest: this.manifest, session });
          return;
        }
        this.showStatus(worldText(leaveMessage(code)));
        this.chat?.destroy();
        this.chat = undefined;
        // No more health comes from a room left behind: no heartbeat beating on.
        this.lowHealth?.destroy();
        this.lowHealth = undefined;
        this.inventoryPanel?.destroy();
        this.inventoryPanel = undefined;
        this.profilePanel?.destroy();
        this.profilePanel = undefined;
        this.bestiaryPanel?.destroy();
        this.bestiaryPanel = undefined;
        this.achievementToast?.destroy();
        this.achievementToast = undefined;
        this.questClient?.destroy();
        this.questClient = undefined;
        this.onlineCount?.destroy();
        this.onlineCount = undefined;
        this.spellBar?.destroy();
        this.spellBar = undefined;
        this.quickPotion?.destroy();
        this.quickPotion = undefined;
        this.spellbook?.destroy();
        this.spellbook = undefined;
        this.touchHud?.destroy();
        this.touchHud = undefined;
        this.mountCast = null;
        this.restartBanner?.destroy();
        this.restartBanner = undefined;
        // After a restart only a new process counts as back: the old one may still answer.
        if (rejoinable(code)) {
          rejoinWhenBack(code === CLOSE_CODE_SERVER_RESTART ? Date.now() : undefined);
        }
      });

      room.onMessage('death', (payload: unknown) => {
        const parsed = DeathEventSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.death');
        this.spellBar?.clearEffects();
        const xp = parsed.data.xpLost;
        this.showStatus(worldText('Tu es tombé…'));
        const shrine =
          this.zone === undefined || this.travel.respawn === null
            ? undefined
            : portalById(this.zone, this.travel.respawn);
        const fell =
          shrine === undefined
            ? xp > 0
              ? uiFormat('Tu es tombé. Tu perds {xp} XP. Tu te relèves au sanctuaire.', { xp })
              : uiText('Tu es tombé. Tu te relèves au sanctuaire.')
            : xp > 0
              ? uiFormat('Tu es tombé. Tu perds {xp} XP. Tu te relèves au portail de {portal}.', {
                  xp,
                  portal: shrine.name,
                })
              : uiFormat('Tu es tombé. Tu te relèves au portail de {portal}.', {
                  portal: shrine.name,
                });
        this.chat?.notice(fell);
        // Beaten by a boss for the first time (its dungeon is the one persistent zone): how to come back stronger.
        if (
          this.zone?.persistent === true &&
          this.bossDefeatTip === undefined &&
          !bossDefeatTipSeen()
        ) {
          this.bossDefeatTip = new BossDefeatTip(() => {
            this.bossDefeatTip = undefined;
          });
        }
        this.time.delayedCall(parsed.data.respawnInMs, () => {
          this.hideStatus();
        });
      });
      room.onMessage('resource', (payload: unknown) => {
        const parsed = ResourceEventSchema.safeParse(payload);
        if (parsed.success) this.spellBar?.setResource(parsed.data);
      });
      // The blow takes the colours of the boss's land: its biome, from the
      // boss the mob is (the state knows which; the event only names the mob).
      const flavourOf = (mobId: string): BiomeId | undefined => {
        const mob = (room.state.mobs as NetMap<NetMob> | undefined)?.get(mobId);
        if (mob === undefined) return undefined;
        // Shefira's fire (ADR 0068) throws up the desert's warm embers.
        return mob.mobId === SHEFIRA_MOB_ID ? 'desert' : bossOf(mob.mobId)?.biome;
      };
      // A shooter's projectile (ADR 0055), homing on the player it was loosed at.
      room.onMessage('mob_shot', (payload: unknown) => {
        const parsed = MobShotEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const mob = (room.state.mobs as NetMap<NetMob> | undefined)?.get(parsed.data.mobId);
        const kind = MobIdSchema.safeParse(mob?.mobId);
        const projectile = kind.success ? MOBS[kind.data].ranged?.projectile : undefined;
        const from = this.rendered.get(`mob:${parsed.data.mobId}`);
        if (projectile === undefined || from === undefined) return;
        const target = parsed.data.target;
        this.cueOn('sfx.shot', `mob:${parsed.data.mobId}`);
        flyProjectile(
          this,
          projectile,
          { x: from.x, y: from.y },
          () => this.rendered.get(target),
          parsed.data.ms,
        );
      });
      // A monster turned on us: a « ! » over its head.
      room.onMessage('mob_aggro', (payload: unknown) => {
        const parsed = MobAggroEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.views.get(`mob:${parsed.data.mobId}`)?.alertAggro();
      });
      room.onMessage('boss_telegraph', (payload: unknown) => {
        const parsed = BossTelegraphEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.aerie?.telegraph(parsed.data);
        // A stunned boss's attack put off (ADR 0048) is the same warning, heard once.
        const fresh = this.bossEffects?.telegraph(parsed.data, flavourOf(parsed.data.mobId));
        if (fresh === true) this.cueOn('sfx.boss-telegraph', `mob:${parsed.data.mobId}`);
      });
      room.onMessage('boss_impact', (payload: unknown) => {
        const parsed = BossImpactEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
        this.bossEffects?.impact(parsed.data, me, flavourOf(parsed.data.mobId));
        cueAt('sfx.boss-impact', this.tileCentre(parsed.data.centre), this.listener());
      });
      // Its thresholds, shouted over its head: the kin are coming, or it rages.
      room.onMessage('boss_respawn', (payload: unknown) => {
        const parsed = BossRespawnEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.bossWaking = { bossId: parsed.data.bossId, at: Date.now() + parsed.data.ms };
      });
      room.onMessage('boss_phase', (payload: unknown) => {
        const parsed = BossPhaseEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const key = `mob:${parsed.data.mobId}`;
        this.cueOn('sfx.boss-roar', key);
        this.floatText(
          key,
          worldText(parsed.data.phase === 'summon' ? 'A MOI !' : 'RAGE !'),
          parsed.data.phase === 'summon' ? 0xf0c050 : 0xff5c5c,
          1200,
        );
      });
      room.onMessage('combat_lock', (payload: unknown) => {
        const parsed = CombatLockEventSchema.safeParse(payload);
        if (parsed.success) this.setCombatFor(parsed.data.ms);
      });
      room.onMessage('mount_cast', (payload: unknown) => {
        const parsed = MountCastEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.mountCast =
          parsed.data.durationMs > 0
            ? { startedAt: this.time.now, durationMs: parsed.data.durationMs }
            : null;
      });
      room.onMessage('cast_rejected', (payload: unknown) => {
        const parsed = CastRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.refusals.show(castRefusal(parsed.data.reason));
      });
      room.onMessage('cast', (payload: unknown) => {
        const parsed = CastEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const spell = getSpell(parsed.data.spellId);
        if (spell !== undefined) this.cueOn(`sfx.spell-${spell.fx}`, parsed.data.id);
        const shake = spell === undefined ? null : spellShake(spell);
        if (shake !== null && parsed.data.id === room.sessionId) this.juice.jolt(shake);
        const rendered = this.rendered.get(parsed.data.id);
        if (spell !== undefined && rendered !== undefined) {
          // A copy: the sprite's own position moves on as it catches up with a leap.
          const caster = { x: rendered.x, y: rendered.y };
          const targetId = parsed.data.targetId;
          const target = targetId === undefined ? undefined : this.rendered.get(`mob:${targetId}`);
          const { leapTo, tiles } = parsed.data;
          playSpellFx(this, spell.fx, {
            caster,
            ...(target === undefined ? {} : { target: { x: target.x, y: target.y } }),
            ...(leapTo === undefined ? {} : { leapTo: tileFeet(leapTo) }),
            ...(tiles === undefined ? {} : { line: tiles.map(tileFeet) }),
            reach: spell.range,
          });
        }
        // A charge moved the player at once: the prediction follows now, not after its lag allowance.
        if (parsed.data.leapTo !== undefined && parsed.data.id === room.sessionId) {
          this.localMovement.snap(parsed.data.leapTo);
        }
        // The spell's name over the caster, and a heal's number in green.
        if (spell !== undefined)
          this.floatText(parsed.data.id, spell.name.toUpperCase(), 0xf0c050, 700);
        const healed = parsed.data.healed ?? 0;
        if (healed > 0) this.floatText(parsed.data.id, `+${String(healed)}`, 0x9ece6a, 900);
      });
      // Grimoire (ADR 0048): the spells known and the bar, a spell just learnt.
      room.onMessage('spells', (payload: unknown) => {
        const parsed = SpellsEventSchema.safeParse(payload);
        if (!parsed.success) return;
        this.spellBar?.setSpells(parsed.data);
        this.spellbook?.setSpells(parsed.data);
        const learnt = parsed.data.learnt ?? [];
        if (learnt.length === 0) return;
        cue('sfx.level-up', 0.8);
        this.achievementToast?.showSpells(learnt);
        for (const id of learnt) {
          const name = getSpell(id)?.name;
          if (name !== undefined) this.chat?.notice(uiFormat('Nouveau sort : {name}', { name }));
        }
      });
      room.onMessage('spell_bar_rejected', (payload: unknown) => {
        const parsed = SpellBarRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.spellbook?.showRefusal(parsed.data.reason);
      });
      // A timed state (ADR 0048), drawn on whoever carries it for as long as it lasts.
      room.onMessage('status', (payload: unknown) => {
        const parsed = StatusEventSchema.safeParse(payload);
        if (!parsed.success) return;
        const key = parsed.data.target === 'mob' ? `mob:${parsed.data.id}` : parsed.data.id;
        this.views.get(key)?.setStatus(parsed.data.status, parsed.data.durationMs);
        const status = parsed.data.status;
        if (parsed.data.target === 'mob')
          this.targetFrame?.noteStatus(parsed.data.id, status, parsed.data.durationMs);
        if (
          parsed.data.target === 'player' &&
          parsed.data.id === room.sessionId &&
          isPlayerStatus(status)
        )
          this.spellBar?.showEffect(status, parsed.data.durationMs);
      });

      this.spellBar?.destroy();
      this.spellBar = new SpellBar({
        onCast: (spellId) => {
          room.send('cast', { spellId });
        },
        onOpenGrimoire: () => {
          this.spellbook?.open();
        },
        onMount: () => {
          this.toggleMount();
        },
      });
      this.refreshTouchMount();
      if (this.progress !== undefined) this.spellBar.setProgress(this.progress);
      this.quickPotion?.destroy();
      this.quickPotion = new QuickPotion({
        onDrink: (itemId) => {
          room.send('use_item', { itemId });
        },
        onFull: () => {
          cue('sfx.error');
          this.refusals.show('Ta vie est déjà pleine.');
        },
      });
      this.spellBar.addSlot(this.quickPotion.barSlot);
      this.quickPotion.setIcons(this.itemIcons);
      if (this.inventory !== undefined) this.quickPotion.setInventory(this.inventory);
      this.spellbook?.destroy();
      this.spellbook = new SpellbookPanel({
        classId: this.session.character.classId,
        onSetBar: (bar) => {
          room.send('set_spell_bar', { bar });
        },
      });
      if (this.progress !== undefined) this.spellbook.setLevel(this.progress.level);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.spellBar?.destroy();
        this.spellBar = undefined;
        this.quickPotion?.destroy();
        this.quickPotion = undefined;
        this.spellbook?.destroy();
        this.spellbook = undefined;
      });

      // The mount key (W by default) gets on the worn mount and off it.
      const onMountKey = (event: KeyboardEvent): void => {
        if (event.repeat || !isAction(event, 'mount')) return;
        if (document.activeElement instanceof HTMLInputElement) return;
        event.preventDefault();
        this.toggleMount();
      };
      window.addEventListener('keydown', onMountKey);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        window.removeEventListener('keydown', onMountKey);
      });

      const onTargetKey = (event: KeyboardEvent): void => {
        if (event.repeat || !isAction(event, 'target')) return;
        if (document.activeElement instanceof HTMLInputElement) return;
        event.preventDefault();
        this.targetOrAttack();
      };
      window.addEventListener('keydown', onTargetKey);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        window.removeEventListener('keydown', onTargetKey);
      });

      // The next target (Tab) and the interaction key: the whole game without
      // the mouse. Tab stays the browser's in a field, and the page's focus
      // stays on the game elsewhere.
      const onAimKey = (event: KeyboardEvent): void => {
        if (isTypingTarget(document.activeElement)) return;
        const next = isAction(event, 'targetNext');
        if (!next && !isAction(event, 'interact')) return;
        event.preventDefault();
        if (event.repeat) return;
        if (next) this.targetNext();
        else this.interact();
      };
      window.addEventListener('keydown', onAimKey);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        window.removeEventListener('keydown', onAimKey);
      });

      // The walking keys (the arrows by default): `update` asks the steps.
      // A cinematic swallows key presses before they get here; the key-ups
      // are heard in the capture phase, ahead of it, so no key stays held.
      const onWalkKeyDown = (event: KeyboardEvent): void => {
        const direction = walkDirectionOf(event);
        if (direction === undefined || isTypingTarget(document.activeElement)) return;
        event.preventDefault();
        if (this.keyboardWalk.press(event.code, direction)) this.dropIntentions();
      };
      const onWalkKeyUp = (event: KeyboardEvent): void => {
        this.keyboardWalk.release(event.code);
      };
      const onBlur = (): void => {
        this.keyboardWalk.releaseAll();
      };
      window.addEventListener('keydown', onWalkKeyDown);
      window.addEventListener('keyup', onWalkKeyUp, { capture: true });
      window.addEventListener('blur', onBlur);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        window.removeEventListener('keydown', onWalkKeyDown);
        window.removeEventListener('keyup', onWalkKeyUp, { capture: true });
        window.removeEventListener('blur', onBlur);
        this.keyboardWalk.releaseAll();
      });

      // The mount key as a button, for a finger (ADR 0043); hidden on a desktop.
      this.touchHud = new TouchHud({
        onMount: () => {
          this.toggleMount();
        },
      });
      this.touchHud.addPotion(this.quickPotion.touchSlot);
      this.refreshTouchMount();
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.touchHud?.destroy();
        this.touchHud = undefined;
      });

      this.openChat();
      this.tutorPanel?.destroy();
      this.tutorPanel = undefined;
      if (this.zone?.id === TUTORIAL_ZONE_ID) {
        this.tutorPanel = new TutorPanel({
          inventory: () => this.inventory,
          onSkip: () => {
            room.send('tutor_skip', {});
          },
        });
      }
      room.onMessage('tutor_skip_offer', (payload: unknown) => {
        const parsed = TutorSkipOfferSchema.safeParse(payload);
        if (parsed.success) this.tutorPanel?.setSkipAvailable(parsed.data.available);
      });
      room.onMessage('tutor_skip_rejected', (payload: unknown) => {
        const parsed = TutorSkipRejectedSchema.safeParse(payload);
        if (!parsed.success) return;
        cue('sfx.error');
        this.tutorPanel?.showRefusal(parsed.data.reason);
      });
      // The icons are a CSS background for the DOM panel, so they are loaded
      // outside Phaser; the panel simply shows letters until they arrive.
      void loadSpriteAtlas('icon.potion').then((icons) => {
        this.itemIcons = icons;
        this.quickPotion?.setIcons(icons);
        this.openInventory(icons);
        this.tutorPanel?.setIcons(icons);
      });
      this.profilePanel?.destroy();
      this.achievementToast?.destroy();
      this.achievementToast = new AchievementToast();
      this.questClient?.destroy();
      this.questClient = new QuestClient({
        send: (type, payload) => {
          this.room?.send(type, payload);
        },
        notice: (text) => {
          this.chat?.notice(text);
        },
        refuse: (text) => {
          this.refusals.show(text);
        },
        icons: () => this.inventoryPanel?.icons ?? null,
      });
      if (this.progress !== undefined) this.questClient.setLevel(this.progress.level);
      this.campCoach?.destroy();
      this.campCoach = undefined;
      if (this.zone?.id === TUTORIAL_ZONE_ID) {
        this.campCoach = new CampCoach({
          journal: this.questClient.log,
          inventory: () => this.inventory,
          greet: () => this.greetNewcomer(),
          characterId: this.session.character.id,
        });
      }
      this.profilePanel = new ProfilePanel({
        icons: () => this.itemIcons,
        session: this.session,
        portalCount: this.zone?.portals.length ?? 0,
        onRequest: () => {
          this.room?.send('profile', {});
        },
        onAchievements: () => {
          this.room?.send('achievements', {});
        },
        onWearTitle: (achievementId) => {
          this.room?.send('set_title', { achievementId });
        },
        onClaimReward: (achievementId) => {
          this.room?.send('claim_achievement', { achievementId });
        },
      });
      if (this.inventory !== undefined) {
        this.profilePanel.setEquipped(this.inventory.equipped);
        this.profilePanel.setGold(this.inventory.gold);
      }
      if (this.progress !== undefined) this.profilePanel.setProgress(this.progress);
      this.profilePanel.setPortalsFound(this.travel.discovered.length);
      // The hunting log (ADR 0093), read from the same career.
      this.bestiaryPanel?.destroy();
      this.bestiaryPanel = new BestiaryPanel({
        icons: () => this.itemIcons,
        creatureFrame: (bodyKey) => this.creatureFrame(bodyKey),
        onRequest: () => {
          this.room?.send('profile', {});
        },
      });
      // Back from linking Google or Steam (ADR 0054): the account tab says how it went.
      if (hasLinkNotice()) this.profilePanel.openAccount();
      setRegistrationOpener(() => {
        this.profilePanel?.openAccount();
      });
      // Beside the home button, which the profile panel has just put up;
      // the gear goes last, right of the profile.
      showSoundToggle();
      showDiscordButton();
      showSettingsPanel();
      preloadSounds(SFX_KEYS);
      // After the profile panel: the count goes first in the top row.
      this.onlineCount?.destroy();
      this.onlineCount = new OnlineCount();
    } catch (error) {
      // What went wrong shows on the canvas, under the loading screen.
      hideLoader();
      const message = error instanceof Error ? error.message : String(error);
      if (
        this.session.character.zoneId !== DEFAULT_ZONE_ID &&
        (message.includes('instance_gone') || message.includes('not_admitted'))
      ) {
        // The instance closed while away (ADR 0068): the world puts the
        // character back before its gate, from what the server saved.
        const { instanceId: _gone, ...character } = this.session.character;
        const session = { ...this.session, character: { ...character, zoneId: DEFAULT_ZONE_ID } };
        saveSession(session);
        this.scene.restart({ manifest: this.manifest, session });
        return;
      }
      if (message.includes('token')) {
        clearSession();
        this.showStatus(worldText('Session expirée. Recharge la page.'));
      } else {
        // The raw error can hold characters the font has no glyph for, and
        // says nothing a player can act on; it goes to the console instead.
        console.error('[shefira] connexion refusée', message);
        this.showStatus(worldText('Connexion impossible. Le serveur tourne ?'));
      }
    }
  }

  /** The inventory panel: intents out, the server's inventory in. */
  /** Shefira's Aire (ADR 0068): what the scene lends it of the room and the entities it draws. */
  private openAerie(zone: ZoneDefinition): AerieClient | undefined {
    const room = this.room;
    if (room === undefined) return undefined;
    const players = (): NetMap<NetPlayer> | undefined => room.state.players;
    return new AerieClient({
      scene: this,
      manifest: this.manifest,
      zone,
      room,
      isOpen: (tile) => this.grid.isWalkableTile(tile),
      me: () => {
        const me = players()?.get(room.sessionId);
        return me === undefined || me.hp <= 0 ? undefined : { x: me.x, y: me.y };
      },
      drawnAt: (key) => this.rendered.get(key),
      viewOf: (key) => this.views.get(key),
      mob: (id) => (room.state.mobs as NetMap<NetMob> | undefined)?.get(id),
      nameOf: (sessionId) => players()?.get(sessionId)?.name,
      walkTo: (tile) => this.walkTo(tile),
      notice: (text) => {
        this.chat?.notice(text);
      },
      floatText: (key, text, colour, ms) => {
        this.floatText(key, text, colour, ms);
      },
      jolt: (kind) => {
        this.juice.jolt(kind);
      },
      listener: () => this.listener(),
    });
  }

  /** An admin's world map teleports where it is clicked (ADR 0025). */
  private armMapTeleport(): void {
    if (!this.admin) return;
    this.worldMap?.setPick((tile) => {
      this.room?.send('admin_command', { action: 'teleport', ...tile });
    });
    this.worldMap?.setOnOpen(() => {
      this.room?.send('admin_command', { action: 'list_players' });
    });
  }

  /**
   * The game master's window, once the server has said this account is an
   * admin, with the item icons loaded like the inventory's.
   */
  private openAdminPanel(): void {
    const room = this.room;
    if (room === undefined || this.adminPanel !== undefined) return;
    void loadSpriteAtlas('icon.potion').then((icons) => {
      if (this.adminPanel !== undefined || this.room !== room) return;
      this.adminPanel = new AdminPanel({
        icons,
        token: this.session.token,
        onCommand: (command) => {
          room.send('admin_command', command);
        },
      });
      if (this.inventory !== undefined) this.adminPanel.setInventory(this.inventory);
      if (this.progress !== undefined) this.adminPanel.setLevel(this.progress.level);
    });
  }

  /** A creature's idle frame facing south, for the hunting log; null until its atlas has loaded. */
  private creatureFrame(bodyKey: string): AtlasFrame | null {
    const sheet = this.manifest.sheets[bodyKey];
    if (sheet === undefined || !this.textures.exists(sheet.atlas)) return null;
    const texture = this.textures.get(sheet.atlas);
    const name = `${sheet.prefix}/body/idle/south/0`;
    if (!texture.has(name)) return null;
    const frame = texture.get(name);
    return {
      image: frame.source.image as CanvasImageSource,
      x: frame.cutX,
      y: frame.cutY,
      width: frame.cutWidth,
      height: frame.cutHeight,
    };
  }

  private openInventory(icons: CssAtlas | null): void {
    const room = this.room;
    if (room === undefined) return;
    this.inventoryPanel?.destroy();
    const bodyKey = `body.${this.session.character.classId}`;
    const sheet = this.manifest.sheets[bodyKey];
    if (sheet === undefined) return;
    // The body and its overlays in the class's atlas; the companions the
    // shop and the wardrobe show live with the other creatures.
    const textures = [this.textures.get(sheet.atlas), this.textures.get('creatures')];
    const frameOf = (name: string): AtlasFrame | null => {
      const texture = textures.find((candidate) => candidate.has(name));
      if (texture === undefined) return null;
      const frame = texture.get(name);
      const image = frame.source.image as CanvasImageSource;
      return {
        image,
        x: frame.cutX,
        y: frame.cutY,
        width: frame.cutWidth,
        height: frame.cutHeight,
      };
    };
    this.inventoryPanel = new InventoryPanel({
      classId: this.session.character.classId,
      bodyKey: sheet.prefix,
      frame: frameOf,
      featureTints: lookTints(this.session.character.look),
      hairStyle: this.session.character.look.hairStyle,
      body: this.session.character.look.body,
      icons,
      onEquip: (itemId) => {
        room.send('equip', { itemId });
      },
      onUnequip: (slot) => {
        room.send('unequip', { slot });
      },
      onMove: (from, to) => {
        room.send('move_item', { from, to });
      },
      onSort: () => {
        room.send('sort_bag', {});
      },
      onUse: (itemId) => {
        room.send('use_item', { itemId });
      },
      onDestroy: (itemId, quantity) => {
        room.send('destroy_item', { itemId, quantity });
      },
      onLink: (item) => {
        this.chat?.linkItem(item);
      },
    });
    // The atlas can land after the first inventory did: seed the panel with
    // whatever the server has already said rather than waiting for a change.
    if (this.inventory !== undefined) this.inventoryPanel.setInventory(this.inventory);
    if (this.progress !== undefined) this.inventoryPanel.setProgress(this.progress);

    this.smithPanel?.destroy();
    this.fusionPanel?.destroy();
    this.fusionPanel = new FusionPanel({
      icons,
      onFuse: (templateId) => {
        room.send('fuse_item', { templateId });
      },
      onEquip: (itemId) => {
        room.send('equip', { itemId });
      },
    });
    if (this.inventory !== undefined) this.fusionPanel.setInventory(this.inventory);

    this.alchemyPanel?.destroy();
    this.alchemyPanel = new AlchemyPanel({
      icons,
      onBrew: (potion, count) => {
        room.send('brew_potion', { potion, count });
      },
    });
    if (this.inventory !== undefined) this.alchemyPanel.setInventory(this.inventory);

    this.smithPanel = new SmithPanel({
      icons,
      onUpgrade: (itemId) => {
        room.send('upgrade_item', { itemId });
      },
    });
    if (this.inventory !== undefined) this.smithPanel.setInventory(this.inventory);

    this.runesmithPanel?.destroy();
    this.runesmithPanel = new RunesmithPanel({
      icons,
      onReforge: (itemId, choices) => {
        room.send('reforge_item', { itemId, ...choices });
      },
    });
    if (this.inventory !== undefined) this.runesmithPanel.setInventory(this.inventory);

    this.merchantPanel?.destroy();
    this.merchantPanel = new MerchantPanel({
      icons,
      biome: () => {
        const merchant = (room.state.mobs as NetMap<NetMob> | undefined)?.get(
          this.merchantId ?? '',
        );
        return merchant === undefined || this.zone === undefined
          ? undefined
          : biomeAt(this.zone, merchant);
      },
      zoneId: () => this.zone?.id,
      wearer: () => ({
        level: this.progress?.level ?? 1,
        classId: this.session.character.classId,
      }),
      onBuy: (templateId, quantity) => {
        room.send('buy_item', { templateId, quantity });
      },
      onSell: (itemId, quantity) => {
        room.send('sell_item', { itemId, quantity });
      },
    });
    if (this.inventory !== undefined) this.merchantPanel.setInventory(this.inventory);

    this.stashPanel?.destroy();
    this.stashPanel = new StashPanel({
      icons,
      wearer: () => ({
        level: this.progress?.level ?? 1,
        classId: this.session.character.classId,
      }),
      onDeposit: (itemId, quantity) => {
        room.send('stash_deposit', { itemId, quantity });
      },
      onWithdraw: (itemId, quantity) => {
        room.send('stash_withdraw', { itemId, quantity });
      },
    });
    if (this.inventory !== undefined) this.stashPanel.setInventory(this.inventory);

    // The shop (ADR 0031), from anywhere: its previews draw the cosmetics
    // and the beasts from the same atlas as the doll. Never in the apps
    // (ADR 0075): the stores forbid selling cosmetics outside their billing.
    this.shopPanel?.destroy();
    this.shopPanel = isNativeApp()
      ? undefined
      : new ShopPanel({
          token: this.session.token,
          characterName: this.session.character.name,
          icons,
          preview: {
            bodyKey: sheet.prefix,
            frame: frameOf,
            effectFrame: (name) => {
              // The trails' sprites (ADR 0073) live in the effects atlas, not the characters'.
              const effects = this.textures.get('effects');
              if (!effects.has(name)) return null;
              const frame = effects.get(name);
              return {
                image: frame.source.image as CanvasImageSource,
                x: frame.cutX,
                y: frame.cutY,
                width: frame.cutWidth,
                height: frame.cutHeight,
              };
            },
            featureTints: lookTints(this.session.character.look),
            hairStyle: this.session.character.look.hairStyle,
            body: this.session.character.look.body,
            prefixOf: (key) => this.manifest.sheets[key]?.prefix,
          },
          onShowInventory: () => {
            this.inventoryPanel?.toggle(true);
            this.inventoryPanel?.showLook();
          },
          onCreateAccount: () => {
            this.profilePanel?.openAccount();
          },
        });
    if (this.inventory !== undefined) this.shopPanel?.setInventory(this.inventory);
    // The auction house (ADR 0035), over HTTP: the room only pushes the bag back.
    this.auctionPanel?.destroy();
    this.auctionPanel = new AuctionPanel({
      token: this.session.token,
      access: async () => (await this.requestAuctionAccess()).ticket,
      icons,
      inventory: () => this.inventory,
      wearer: () => ({
        level: this.progress?.level ?? 1,
        classId: this.session.character.classId,
      }),
    });
    // The ladder (ADR 0082), over HTTP like the auction house.
    this.ladderPanel?.destroy();
    this.ladderPanel = new LadderPanel({ token: this.session.token });
    // Back from Stripe: the address says which order to watch, once.
    const shopReturn = consumeShopReturn();
    if (shopReturn !== null) {
      if ('order' in shopReturn) this.shopPanel?.trackOrder(shopReturn.order);
      else this.shopPanel?.showCancelled();
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.shopPanel?.destroy();
      this.shopPanel = undefined;
      this.auctionPanel?.destroy();
      this.auctionPanel = undefined;
      this.ladderPanel?.destroy();
      this.ladderPanel = undefined;
      for (const pending of this.auctionRequests.values()) {
        window.clearTimeout(pending.timer);
        pending.reject(new Error('L’hôtel des ventes est fermé.'));
      }
      this.auctionRequests.clear();
      this.inventoryPanel?.destroy();
      this.inventoryPanel = undefined;
      this.profilePanel?.destroy();
      this.profilePanel = undefined;
      this.bestiaryPanel?.destroy();
      this.bestiaryPanel = undefined;
      this.smithPanel?.destroy();
      this.smithPanel = undefined;
      this.runesmithPanel?.destroy();
      this.runesmithPanel = undefined;
      this.fusionPanel?.destroy();
      this.fusionPanel = undefined;
      this.alchemyPanel?.destroy();
      this.alchemyPanel = undefined;
      this.adminPanel?.destroy();
      this.adminPanel = undefined;
      this.merchantPanel?.destroy();
      this.merchantPanel = undefined;
      this.stashPanel?.destroy();
      this.stashPanel = undefined;
      this.onlineCount?.destroy();
      this.onlineCount = undefined;
    });
  }

  /**
   * The panel sends intentions; what is shown comes back from the server like
   * everyone else's lines, so a rejected message never appears as if it went out.
   */
  private openChat(): void {
    const room = this.room;
    if (room === undefined) return;
    this.chat?.destroy();
    this.social?.destroy();
    const chat = new ChatPanel({
      selfSessionId: room.sessionId,
      // A line may be a command (ADR 0033): parsed here, judged by the server.
      onSend: (text, target, links) => {
        this.social?.run(parseChatLine(text), target, links);
      },
    });
    this.chat = chat;
    this.social = new SocialClient(
      room,
      chat,
      () => ({
        characterId:
          (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId)?.id ?? '',
        zoneId: room.state.zoneId,
      }),
      (positions) => {
        this.friendPositions = positions;
        this.worldMap?.setFriends(positions);
      },
      {
        chat,
        inventory: () => this.inventory,
        inventoryPanel: () => this.inventoryPanel,
        icons: () => this.inventoryPanel?.icons ?? null,
      },
      () => this.admin,
    );

    // Server payloads are validated like any other input: a server bug or an
    // old build must not put junk in the DOM.
    room.onMessage('chat', (payload: unknown) => {
      const parsed = ChatBroadcastSchema.safeParse(payload);
      if (!parsed.success) return;
      // A blocked player's lines are not shown (ADR 0076); the server already stops their private ones.
      if (parsed.data.from !== room.sessionId && this.social?.blocks.isBlocked(parsed.data.name)) {
        return;
      }
      chat.append(parsed.data);
      if (parsed.data.from !== room.sessionId) cue('sfx.chat');
      // A party line may come from another zone: only the zone's lines rise over a head.
      if (parsed.data.channel === 'zone')
        this.views
          .get(parsed.data.from)
          ?.say(bubbleText(parsed.data.text, parsed.data.links), { chat: true });
    });
    // The server's own lines (a +8 at the smith): shown apart from what players say.
    room.onMessage('announcement', (payload: unknown) => {
      const parsed = AnnouncementSchema.safeParse(payload);
      if (parsed.success) chat.announce(serverText(parsed.data));
    });
    // A deploy: said once in the chat, counted down on screen until the room closes.
    room.onMessage('server_restart', (payload: unknown) => {
      const parsed = ServerRestartEventSchema.safeParse(payload);
      if (!parsed.success) return;
      chat.announce(
        'Le serveur redémarre pour une mise à jour : retour en jeu dans quelques instants.',
      );
      this.restartBanner ??= new RestartBanner();
      this.restartBanner.start(parsed.data.seconds);
    });
    room.onMessage('chat_rejected', (payload: unknown) => {
      const parsed = ChatRejectedSchema.safeParse(payload);
      if (parsed.success) chat.rejected(parsed.data);
    });
    const inspectPanel = new InspectPanel(() => this.inventoryPanel?.icons ?? null);
    this.inspectPanel = inspectPanel;
    room.onMessage('inspection', (payload: unknown) => {
      const parsed = InspectionSchema.safeParse(payload);
      if (parsed.success) inspectPanel.show(parsed.data);
    });
    room.onMessage('inspect_rejected', (payload: unknown) => {
      const parsed = InspectRejectedSchema.safeParse(payload);
      if (parsed.success) this.refusals.show("Ce joueur n'est plus près de toi.");
    });
    this.worldEvents = new WorldEventClient(room);
    listenRareMobs(room);
    this.worldEvents.onChange(() => {
      const event = this.worldEvents?.current ?? null;
      this.worldMap?.setEvent(event === null ? null : { x: event.x, y: event.y });
      // The minimap and the music follow at once: the battle has begun, or it is over.
      this.minimapAt = 0;
    });
    this.encounters = new EncounterClient({
      room,
      scene: this,
      manifest: this.manifest,
      icons: () => this.inventoryPanel?.icons ?? null,
      notice: (text) => this.chat?.notice(text),
      refuse: (text) => {
        this.refusals.show(text);
      },
    });
    if (this.inventory !== undefined) this.encounters.setInventory(this.inventory);
    this.bounties = new BountyClient({
      room,
      icons: () => this.inventoryPanel?.icons ?? null,
      notice: (text) => this.chat?.notice(text),
      refuse: (text) => {
        this.refusals.show(text);
      },
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.refusals.destroy();
      this.placeBanner.destroy();
      this.inspectPanel?.destroy();
      this.inspectPanel = undefined;
      this.chat?.destroy();
      this.chat = undefined;
      this.social?.destroy();
      this.social = undefined;
      this.worldEvents?.destroy();
      this.worldEvents = undefined;
      this.encounters?.destroy();
      this.encounters = undefined;
      this.bounties?.destroy();
      this.bounties = undefined;
    });
  }

  /**
   * What a click or a tap asks for, in order: a drop, a townsperson, an arch,
   * a chest, a monster, else a walk. Returns the tile of the
   * walk asked for, if that is what it was, for a held finger to follow.
   */
  private requestMove(pointer: Phaser.Input.Pointer): { x: number; y: number } | undefined {
    if (this.room === undefined) return undefined;
    const target = this.tileUnder(pointer);
    this.dropIntentions();

    // A click on a drop fetches it; on a monster, attacks it. The sprite is
    // taller than its tile, so the tile above the feet counts too.
    const dropId = this.dropAt(target);
    if (dropId !== undefined) {
      this.hideMarker();
      // The server walks to the drop itself; the prediction only follows.
      this.localMovement.stop();
      this.room.send('pickup', { dropId });
      return undefined;
    }
    // A click on a townsperson who answers walks up to them and opens their
    // window; the server checks the distance again at every request.
    const npcId = this.npcUnder(pointer);
    if (npcId !== undefined) {
      this.visitNpc(npcId);
      return undefined;
    }
    // A click on a village's arch walks to it and uses it (ADR 0017).
    const portal = this.portalAt(target);
    if (portal !== undefined) {
      this.visitPortal(portal);
      return undefined;
    }
    const auction = this.zone === undefined ? undefined : auctionHouseAt(this.zone, target);
    if (auction !== undefined) {
      this.visitAuction(auction);
      return undefined;
    }
    // A click on a chest walks up to it and opens it (ADR 0021).
    const chest = this.chestLayer?.chestAt(target);
    if (chest !== undefined) {
      this.visitChest(chest);
      return undefined;
    }
    // A lost chest on the road (ADR 0093): walked up to and opened within reach.
    const roadChest = this.encounters?.chestAt(target);
    if (roadChest !== undefined) {
      this.visitRoadChest(roadChest.id);
      return undefined;
    }
    // In the Aire (ADR 0068): a post of the Company, or Shefira's great body far above her tile.
    const aerie = this.aerie?.click(target, { x: pointer.worldX, y: pointer.worldY });
    if (aerie === 'handled') return undefined;
    if (aerie !== undefined) {
      this.attackMob(aerie.attack);
      return undefined;
    }
    const mobId = this.mobAt(target) ?? this.mobAt({ x: target.x, y: target.y + 1 });
    if (mobId !== undefined) {
      this.attackMob(mobId);
      return undefined;
    }
    // The crevasse of La Brèche (ADR 0071): a hole, so a click anywhere on it
    // walks to the nearest open tile of its rim, and its window opens there.
    const gate = this.zone === undefined ? undefined : gateAt(this.zone, target);
    if (gate !== undefined) {
      const edge = gateApproach(gate, target, (x, y) => this.grid.isWalkableTile({ x, y }));
      if (edge !== undefined && this.aerie?.visitGate(edge) === true) {
        this.moveMarker?.show(edge, 'talk');
        return undefined;
      }
    }
    // A click on a fortress, a cave's mouth or a hatch walks into its way
    // out, like the auction hall's facade: no need to aim at the doorway.
    const door = this.zone === undefined ? undefined : exitBuildingAt(this.zone, target);
    if (door !== undefined && this.walkTo(door)) {
      this.moveMarker?.show(door, 'talk');
      return undefined;
    }
    // A fingertip covers more than a tile at 2x: a tap beside a monster or a
    // drop still means it (ADR 0043). A mouse keeps its exact tile.
    if (pointer.wasTouch) {
      const near = this.nearTap(target);
      if (near?.drop !== undefined) {
        this.hideMarker();
        this.localMovement.stop();
        this.room.send('pickup', { dropId: near.drop });
        return undefined;
      }
      if (near?.mob !== undefined) {
        this.attackMob(near.mob);
        return undefined;
      }
    }
    return this.walkTo(target) ? target : undefined;
  }

  /** Any new click or walking key is a new intention: the walk to a townsperson is dropped. */
  private dropIntentions(): void {
    this.visitingNpc = undefined;
    this.visitingPortal = undefined;
    this.visitingChest = undefined;
    this.visitingRoadChest = undefined;
    this.visitingAuction = undefined;
    this.aerie?.cancelVisit();
  }

  /**
   * The walking keys: a trip straight ahead, asked again before its end, and
   * a stop on the next tile when let go. Like a click on the ground, it gives
   * up the fight and the drop.
   */
  private followKeyboardWalk(): void {
    if (this.room === undefined || this.editor?.active === true) return;
    if (this.cinematics?.active === true) return;
    const movement = this.localMovement;
    const tile = this.keyboardWalk.follow(
      { tile: movement.tile, stepsLeft: movement.stepsLeft, target: movement.target },
      (at) => this.grid.isWalkableTile(at),
      (at) => this.sendWalk(at),
    );
    if (tile === undefined) return;
    this.heldWalk = undefined;
    this.hideMarker();
  }

  /** Asks the server for a walk to `tile`, if it is open and has a route. */
  private walkTo(target: { x: number; y: number }): boolean {
    if (this.room === undefined || !this.grid.isWalkableTile(target)) return false;
    if (!this.sendWalk(target)) return false;
    this.markedMobId = undefined;
    this.moveMarker?.show(target, 'move');
    return true;
  }

  /**
   * Every walk goes out here: validated and started at once on the predicted
   * tile (ADR 0045), with the rules the server applies from its own. A walk
   * with no route, or to where the player already stands, costs no round trip.
   */
  private sendWalk(target: { x: number; y: number }): boolean {
    if (this.room === undefined) return false;
    // The tile the predicted walk turns on: the server turns there too, so
    // both walk the same tiles (ADR 0045).
    const from = this.localMovement.turningTile;
    if (!this.localMovement.walk(this.grid, target, this.time.now)) return false;
    this.room.send('move_to', { x: target.x, y: target.y, from });
    return true;
  }

  /**
   * The drop or the hostile monster nearest a tap that hit neither, within a
   * tile of it (a monster's sprite also rises a tile above its feet).
   */
  private nearTap(target: { x: number; y: number }): { drop?: string; mob?: string } | undefined {
    let best: { drop?: string; mob?: string; distance: number } | undefined;
    for (let dy = -1; dy <= 2; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const tile = { x: target.x + dx, y: target.y + dy };
        // The tile under the feet counts from above: a monster is drawn a
        // tile higher than it stands.
        const distance = dx * dx + (dy - 0.5) ** 2;
        if (best !== undefined && distance >= best.distance) continue;
        const drop = this.dropAt(tile);
        const mob = drop === undefined ? this.mobAt(tile) : undefined;
        if (drop !== undefined || mob !== undefined) best = { drop, mob, distance };
      }
    }
    return best;
  }

  /**
   * A finger held on the ground after a walk: every few frames the walk is
   * asked again towards where it is now, like dragging the hero along.
   */
  private followHeldWalk(time: number): void {
    const held = this.heldWalk;
    if (held === undefined) return;
    if (!this.input.activePointer.isDown) {
      this.heldWalk = undefined;
      return;
    }
    if (time - held.since < HELD_WALK_DELAY_MS) return;
    const tile = this.tileUnder(this.input.activePointer);
    if (tile.x === held.tile.x && tile.y === held.tile.y) return;
    held.since = time - HELD_WALK_DELAY_MS + HELD_WALK_EVERY_MS;
    if (this.walkTo(tile)) held.tile = tile;
  }

  /** Gets on the worn mount, or off it: the mount key and the touch button. */
  private toggleMount(): void {
    if (this.room === undefined) return;
    if (!this.isRiding() && this.inventory?.equipped.mount === undefined) {
      this.refusals.show('Équipe d’abord une monture.');
      return;
    }
    if (!this.isRiding() && this.inCombat()) {
      cue('sfx.error');
      this.refusals.show('Pas de monture en plein combat.');
      return;
    }
    if (!this.isRiding()) {
      // The server halts the walk to saddle up; the prediction must too, or it
      // walks on and is pulled back once the cast ends.
      this.localMovement.stop();
      this.heldWalk = undefined;
      this.moveMarker?.hide();
    }
    this.room.send('mount', {});
  }

  /**
   * The mount buttons (touch HUD, spell bar) show while a mount is worn, lit
   * while riding, darkened like a cooldown while the combat lock refuses W.
   */
  private refreshTouchMount(): void {
    const worn = this.inventory?.equipped.mount !== undefined;
    const riding = this.isRiding();
    this.touchHud?.setMount(worn, riding);
    this.spellBar?.setMount(worn, riding);
    const left = riding ? 0 : Math.max(0, this.combatUntil - Date.now());
    const share = left / TELEPORT_COMBAT_LOCK_MS;
    this.touchHud?.setMountLock(share);
    this.spellBar?.setMountLock(share);
  }

  private attackMob(mobId: string): void {
    if (this.room === undefined) return;
    if (this.isRiding()) {
      // The server ignores an attack from the saddle; say why rather than nothing.
      this.refusals.show(castRefusal('mounted'));
      return;
    }
    const mob = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(mobId);
    if (mob !== undefined) {
      this.markedMobId = mobId;
      this.moveMarker?.show({ x: mob.x, y: mob.y }, 'attack');
    }
    // The server walks into melee range itself; the prediction only follows.
    this.localMovement.stop();
    this.room.send('attack', { targetId: mobId });
  }

  /**
   * Space: the first press picks the nearest hostile monster on screen, the
   * next attacks it. Picking is only this client's highlight; the attack is
   * the same intent a click sends, and the server still judges it.
   */
  private targetOrAttack(): void {
    const picked = this.markedMobId;
    if (picked !== undefined && this.moveMarker?.shown === 'target' && this.isOnScreen(picked)) {
      this.attackMob(picked);
      return;
    }
    const nearest = this.nearestVisibleMob();
    // Already fighting the nearest one: nothing better to pick.
    if (nearest === undefined || (nearest === picked && this.moveMarker?.shown === 'attack'))
      return;
    const mob = this.room?.state.mobs.get(nearest);
    if (mob === undefined) return;
    this.markedMobId = nearest;
    this.moveMarker?.show({ x: mob.x, y: mob.y }, 'target');
  }

  /**
   * Tab: the aim moves to the next hostile monster on screen, nearest first
   * and round again, without attacking; Space then attacks it.
   */
  private targetNext(): void {
    const me = this.localPlayer();
    if (me === undefined) return;
    const aimed = this.moveMarker?.shown === 'target' || this.moveMarker?.shown === 'attack';
    const current = aimed ? this.markedMobId : undefined;
    const next = nextTarget(me, this.visibleHostileMobs(), current);
    if (next === undefined || next === current) return;
    const mob = this.room?.state.mobs.get(next);
    if (mob === undefined) return;
    this.markedMobId = next;
    this.moveMarker?.show({ x: mob.x, y: mob.y }, 'target');
  }

  /**
   * The interaction key: the nearest within reach of a townsperson, a chest,
   * an arch or a drop, used by the same path as a click on it. The server
   * checks the distance again, as for a click.
   */
  private interact(): void {
    const room = this.room;
    const me = this.localPlayer();
    if (room === undefined || me === undefined || this.editor?.active === true) return;
    type Use = () => void;
    const near: Placed<Use>[] = [];
    (room.state.mobs as NetMap<NetMob> | undefined)?.forEach((mob, id) => {
      if (isInteractiveMob(mob.mobId)) {
        near.push({
          x: mob.x,
          y: mob.y,
          value: () => {
            this.visitNpc(id);
          },
        });
      }
    });
    for (const chest of this.zone?.chests ?? []) {
      // An emptied chest has nothing left; what it held lies on the ground in front.
      if (this.chestLayer?.lookOf(chest.id) === 'opened') continue;
      const at = closestTileOf(footprintOf({ prop: 'chest', x: chest.x, y: chest.y }), me);
      near.push({
        ...at,
        value: () => {
          this.visitChest(chest);
        },
      });
    }
    const portal = this.zone === undefined ? undefined : portalInReach(this.zone, me);
    if (portal !== undefined) {
      const at = closestTileOf(footprintOf({ prop: PORTAL_PROP_ID, x: portal.x, y: portal.y }), me);
      near.push({
        ...at,
        value: () => {
          this.visitPortal(portal);
        },
      });
    }
    (room.state.drops as NetMap<NetDrop> | undefined)?.forEach((drop, id) => {
      near.push({
        x: drop.x,
        y: drop.y,
        value: () => {
          this.hideMarker();
          // The server walks to the drop itself; the prediction only follows.
          this.localMovement.stop();
          room.send('pickup', { dropId: id });
        },
      });
    });
    const use = nearestWithin(me, near);
    if (use === undefined) return;
    this.dropIntentions();
    use();
  }

  private localPlayer(): NetPlayer | undefined {
    const room = this.room;
    if (room === undefined) return undefined;
    return (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
  }

  private isOnScreen(mobId: string): boolean {
    const at = this.rendered.get(`mob:${mobId}`);
    return at !== undefined && this.cameras.main.worldView.contains(at.x, at.y);
  }

  /** The living monsters on screen that can be fought: never the peaceful. */
  private visibleHostileMobs(): Placed<string>[] {
    const found: Placed<string>[] = [];
    this.room?.state.mobs.forEach((mob, id) => {
      if (mob.hp <= 0 || !this.isOnScreen(id)) return;
      const parsed = MobIdSchema.safeParse(mob.mobId);
      if (!parsed.success || MOBS[parsed.data].peaceful) return;
      found.push({ x: mob.x, y: mob.y, value: id });
    });
    return found;
  }

  private nearestVisibleMob(): string | undefined {
    const me = this.localPlayer();
    return me === undefined ? undefined : nextTarget(me, this.visibleHostileMobs(), undefined);
  }

  /** Whether the local player is on their mount, as the server last said. */
  private isRiding(): boolean {
    if (this.room === undefined) return false;
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    return me !== undefined && me.mount !== '';
  }

  private hideMarker(): void {
    this.markedMobId = undefined;
    this.moveMarker?.hide();
  }

  private dropAt(tile: { x: number; y: number }): string | undefined {
    if (this.room === undefined) return undefined;
    let found: string | undefined;
    const drops = this.room.state.drops as NetMap<NetDrop> | undefined;
    drops?.forEach((drop, id) => {
      if (found === undefined && drop.x === tile.x && drop.y === tile.y) found = id;
    });
    return found;
  }

  private tileUnder(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    const world = pointer.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
    return { x: Math.floor(world.x / TILE_SIZE), y: Math.floor(world.y / TILE_SIZE) };
  }

  /**
   * The townsperson who answers a click under the pointer (`interactive` in
   * the mob data); their sprite is taller than their tile, so the tile above
   * counts too.
   */
  private npcUnder(pointer: Phaser.Input.Pointer): string | undefined {
    const tile = this.tileUnder(pointer);
    return this.npcAt(tile) ?? this.npcAt({ x: tile.x, y: tile.y + 1 });
  }

  /** Any monster or townsperson standing on a tile, for the world editor (ADR 0022). */
  private inhabitantAt(tile: {
    x: number;
    y: number;
  }): { mob: string; x: number; y: number } | undefined {
    if (this.room === undefined) return undefined;
    let found: { mob: string; x: number; y: number } | undefined;
    (this.room.state.mobs as NetMap<NetMob> | undefined)?.forEach((mob) => {
      if (found === undefined && mob.x === tile.x && mob.y === tile.y) {
        found = { mob: mob.mobId, x: mob.x, y: mob.y };
      }
    });
    return found;
  }

  private npcAt(tile: { x: number; y: number }): string | undefined {
    if (this.room === undefined) return undefined;
    let found: string | undefined;
    (this.room.state.mobs as NetMap<NetMob> | undefined)?.forEach((mob, id) => {
      if (found !== undefined || mob.x !== tile.x || mob.y !== tile.y) return;
      if (isInteractiveMob(mob.mobId)) found = id;
    });
    return found;
  }

  /** The smelter's window: one at a time, the others close. */
  private openSmelter(smelterId: string): void {
    this.smelterId = smelterId;
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.stashPanel?.close();
    this.alchemyPanel?.close();
    this.fusionPanel?.open();
  }

  /** The alchemist's window: his biome says which potions; one window at a time. */
  private openAlchemist(alchemistId: string): void {
    if (this.room === undefined) return;
    const alchemist = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(alchemistId);
    const biome =
      alchemist === undefined || this.zone === undefined
        ? undefined
        : biomeAt(this.zone, alchemist);
    if (biome === undefined) return;
    this.alchemistId = alchemistId;
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.stashPanel?.close();
    this.fusionPanel?.close();
    this.alchemyPanel?.open(biome);
  }

  /**
   * A keeper's window follows the player's reach, as the server judges it:
   * walked out of it, the window closes rather than waiting greyed out.
   */
  private followReach(
    panel: { setInReach(inReach: boolean): void; close(): void },
    inReach: boolean,
  ): void {
    if (inReach) panel.setInReach(true);
    else panel.close();
  }

  /** The quests' dialogue closes too once its townsperson is out of reach. */
  private updateDialogReach(): void {
    const npcId = this.questClient?.dialogNpcId;
    if (npcId === undefined || this.room === undefined) return;
    const npc = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(npcId);
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    if (me === undefined) return;
    if (npc === undefined || Math.max(Math.abs(me.x - npc.x), Math.abs(me.y - npc.y)) > QUEST_REACH)
      this.questClient?.closeDialog();
  }

  /** While the alchemist's window is open: whether the player stands by him. */
  private updateAlchemistReach(): void {
    if (this.alchemyPanel?.isOpen !== true || this.room === undefined) return;
    const alchemist = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(
      this.alchemistId ?? '',
    );
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.alchemyPanel,
      alchemist !== undefined &&
        me !== undefined &&
        withinAlchemistReach({ x: me.x, y: me.y }, [alchemist]),
    );
  }

  /** While the tutor's window is open: whether the player stands by him. */
  private updateTutorReach(): void {
    if (this.tutorPanel?.isOpen !== true || this.room === undefined) return;
    const tutor = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(this.tutorId ?? '');
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.tutorPanel,
      tutor !== undefined && me !== undefined && withinTutorReach({ x: me.x, y: me.y }, [tutor]),
    );
  }

  /** While the smelter's window is open: whether the player stands by him. */
  private updateSmelterReach(): void {
    if (this.fusionPanel?.isOpen !== true || this.room === undefined) return;
    const smelter = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(this.smelterId ?? '');
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.fusionPanel,
      smelter !== undefined &&
        me !== undefined &&
        withinSmelterReach({ x: me.x, y: me.y }, [smelter]),
    );
  }

  /**
   * A newcomer arrives beside the tutor (ADR 0078): his first quest opens by
   * itself, as if they had clicked him. False while he is not in sight yet.
   */
  private greetNewcomer(): boolean {
    const mobs = this.room?.state.mobs;
    if (mobs === undefined) return false;
    let tutorId: string | undefined;
    mobs.forEach((mob, id) => {
      if (mob.mobId === TUTOR_MOB_ID) tutorId = id;
    });
    if (tutorId === undefined) return false;
    this.visitNpc(tutorId);
    return true;
  }

  /**
   * Walk to the tile in front of a townsperson under a green square, as a
   * click on a monster walks up to it; their window opens on arrival
   * (`updateNpcVisit`), or at once when already beside them.
   */
  private visitNpc(npcId: string): void {
    const room = this.room;
    if (room === undefined) return;
    const npc = (room.state.mobs as NetMap<NetMob> | undefined)?.get(npcId);
    if (npc === undefined) return;
    const front = { x: npc.x, y: npc.y + 1 };
    const target = this.grid.isWalkableTile(front) ? front : { x: npc.x, y: npc.y };
    this.visitingNpc = { id: npcId, front: target };
    this.markedMobId = npcId;
    this.moveMarker?.show({ x: npc.x, y: npc.y }, 'talk');
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined && Math.max(Math.abs(me.x - npc.x), Math.abs(me.y - npc.y)) <= 2) {
      this.openNpc(npcId);
      return;
    }
    this.sendWalk(target);
  }

  /**
   * A journey (ADR 0017): the flames rise first, then the intent goes out,
   * so the jump the server makes happens behind them. A refusal, or no
   * answer at all, lets them fall again.
   */
  private travelTo(portalId: string): void {
    const room = this.room;
    if (room === undefined || this.veil?.shown === true || this.inCombat()) return;
    this.travelPanel?.close();
    playTeleport();
    const veil = this.veil;
    if (veil === undefined) {
      room.send('teleport', { portalId });
      return;
    }
    void veil.cover().then(() => {
      room.send('teleport', { portalId });
      this.veilTimer?.remove();
      this.veilTimer = this.time.delayedCall(4000, () => {
        this.clearVeil(0);
      });
    });
  }

  /** In combat for `ms` more, as the server says: the red frame, and no journey meanwhile. */
  private setCombatFor(ms: number): void {
    this.combatUntil = Date.now() + ms;
    this.combatFrame?.show(ms);
    this.travelPanel?.setCombatUntil(this.combatUntil);
  }

  private inCombat(): boolean {
    return Date.now() < this.combatUntil;
  }

  private clearVeil(afterMs: number): void {
    this.veilTimer?.remove();
    this.veilTimer = this.time.delayedCall(afterMs, () => {
      this.veilTimer = undefined;
      this.veil?.uncover();
    });
  }

  /** The portal whose arch covers a tile: its footprint and the stone rising above it. */
  private portalAt(tile: { x: number; y: number }): ZonePortal | undefined {
    const rise = PROPS[PORTAL_PROP_ID]?.rise ?? 1;
    return this.zone?.portals.find((portal) => {
      const box = footprintOf({ prop: PORTAL_PROP_ID, x: portal.x, y: portal.y });
      return tile.x >= box.x0 && tile.x <= box.x1 && tile.y > portal.y - rise && tile.y <= box.y1;
    });
  }

  /** Walk to the tile in front of an arch; it is used on arrival, or at once when already there. */
  private visitPortal(portal: ZonePortal): void {
    const room = this.room;
    if (room === undefined) return;
    this.markedMobId = undefined;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (
      me !== undefined &&
      this.zone !== undefined &&
      portalInReach(this.zone, me)?.id === portal.id
    ) {
      this.usePortal(portal);
      return;
    }
    this.visitingPortal = portal;
    this.moveMarker?.show(portal.arrival, 'talk');
    this.sendWalk(portal.arrival);
  }

  /** The facade is clickable; the room grants access only once we reach its door. */
  private visitAuction(hall: { prop: string; x: number; y: number }): void {
    const room = this.room;
    const zone = this.zone;
    if (room === undefined || zone === undefined) return;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined) {
      const near = auctionHouseInReach(zone, me);
      if (near?.x === hall.x && near.y === hall.y && near.prop === hall.prop) {
        this.openAuction();
        return;
      }
    }
    this.visitingAuction = hall;
    const front = { x: hall.x, y: hall.y + 1 };
    this.moveMarker?.show(front, 'talk');
    this.sendWalk(front);
  }

  private updateAuctionVisit(): void {
    const zone = this.zone;
    const room = this.room;
    if (zone === undefined || room === undefined) return;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me === undefined) return;
    const near = auctionHouseInReach(zone, me);
    if (this.auctionPanel?.isOpen === true && near === undefined) this.auctionPanel.close();
    const visit = this.visitingAuction;
    if (
      visit !== undefined &&
      near?.x === visit.x &&
      near.y === visit.y &&
      near.prop === visit.prop
    ) {
      this.openAuction();
    }
  }

  private openAuction(): void {
    this.visitingAuction = undefined;
    this.hideMarker();
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.stashPanel?.close();
    this.fusionPanel?.close();
    this.alchemyPanel?.close();
    void this.requestAuctionAccess()
      .then(({ biome }) => {
        const room = this.room;
        const me = room === undefined ? undefined : room.state.players.get(room.sessionId);
        const near =
          me === undefined || this.zone === undefined
            ? undefined
            : auctionHouseInReach(this.zone, me);
        if (near === undefined || auctionBiomeOf(near) !== biome) return;
        cue('sfx.open');
        this.auctionPanel?.open(biome);
      })
      .catch((error: unknown) => {
        this.chat?.notice(
          error instanceof Error ? error.message : 'Hôtel des ventes indisponible.',
        );
      });
  }

  private requestAuctionAccess(): Promise<{ ticket: string; biome: BiomeId }> {
    const room = this.room;
    if (room === undefined) return Promise.reject(new Error('Hôtel des ventes indisponible.'));
    const requestId = ++this.auctionRequestId;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.auctionRequests.delete(requestId);
        reject(new Error('L’hôtel des ventes ne répond pas.'));
      }, 4000);
      this.auctionRequests.set(requestId, { resolve, reject, timer });
      room.send('auction_access', { requestId });
    });
  }

  private updatePortalVisit(): void {
    const room = this.room;
    if (room === undefined || this.zone === undefined) return;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me === undefined) return;
    const here = portalInReach(this.zone, me);
    this.travelPanel?.setHere(here?.id);
    const visit = this.visitingPortal;
    if (visit === undefined) return;
    // Beside the arch, or on the tile walked to: close enough, as the server judges.
    const arrived = me.x === visit.arrival.x && me.y === visit.arrival.y;
    const near =
      here?.id === visit.id &&
      Math.max(Math.abs(me.x - visit.arrival.x), Math.abs(me.y - visit.arrival.y)) < PORTAL_REACH;
    if (arrived || near) this.usePortal(visit);
  }

  /** Use the arch: the server remembers it and answers with the journeys; the window opens. */
  private usePortal(portal: ZonePortal): void {
    this.visitingPortal = undefined;
    this.hideMarker();
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.stashPanel?.close();
    this.fusionPanel?.close();
    this.alchemyPanel?.close();
    this.room?.send('use_portal', { portalId: portal.id });
    cue('sfx.open');
    this.travelPanel?.open();
  }

  /** Walk in front of a chest; it is opened on arrival, or at once when already touching it. */
  private visitChest(chest: ChestPlacement): void {
    const room = this.room;
    if (room === undefined) return;
    this.markedMobId = undefined;
    if (this.chestLayer?.lookOf(chest.id) === 'opened') {
      this.refusals.show('Ce coffre est vide.');
      return;
    }
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined && touches(chest, me)) {
      this.openChest(chest);
      return;
    }
    const front = ChestLayer.frontOf(chest);
    this.visitingChest = chest;
    this.moveMarker?.show(front, 'talk');
    this.sendWalk(front);
  }

  private updateChestVisit(): void {
    const visit = this.visitingChest;
    const room = this.room;
    if (visit === undefined || room === undefined) return;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined && touches(visit, me)) this.openChest(visit);
  }

  /** Walk to a lost chest; it is asked open once within reach (ADR 0093). */
  private visitRoadChest(encounterId: string): void {
    const room = this.room;
    const chest = this.encounters?.encounters.find((encounter) => encounter.id === encounterId);
    if (room === undefined || chest === undefined) return;
    this.markedMobId = undefined;
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined && this.encounters?.reaches(chest, me) === true) {
      this.encounters.open(chest);
      return;
    }
    this.visitingRoadChest = encounterId;
    this.moveMarker?.show(chest, 'talk');
    this.sendWalk(chest);
  }

  private updateRoadChestVisit(): void {
    const id = this.visitingRoadChest;
    const room = this.room;
    if (id === undefined || room === undefined) return;
    const chest = this.encounters?.encounters.find((encounter) => encounter.id === id);
    if (chest === undefined) {
      this.visitingRoadChest = undefined;
      return;
    }
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me === undefined || this.encounters?.reaches(chest, me) !== true) return;
    this.visitingRoadChest = undefined;
    this.hideMarker();
    this.encounters.open(chest);
  }

  /** While the peddler's window is open: whether the player stands by him, as the server will judge. */
  private updatePeddlerReach(): void {
    const encounters = this.encounters;
    if (encounters?.peddlerOpen !== true || this.room === undefined) return;
    const site = encounters.peddlerSite();
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    if (site === undefined || me === undefined) return;
    if (!withinEncounterReach(me, site)) encounters.closePeddler();
  }

  /** Ask the server; what it holds, or why not, comes back as a message. */
  private openChest(chest: ChestPlacement): void {
    this.visitingChest = undefined;
    this.hideMarker();
    this.room?.send('open_chest', { chestId: chest.id });
  }

  /** Opens a townsperson's window once the player is beside them or on the tile walked to. */
  private updateNpcVisit(): void {
    const visit = this.visitingNpc;
    if (visit === undefined || this.room === undefined) return;
    const npc = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(visit.id);
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    if (npc === undefined || me === undefined) return;
    const beside = Math.max(Math.abs(me.x - npc.x), Math.abs(me.y - npc.y)) <= 1;
    const arrived = me.x === visit.front.x && me.y === visit.front.y;
    if (beside || arrived) this.openNpc(visit.id);
  }

  /**
   * The window a townsperson opens: one at a time, the other closes. A
   * keeper of the quests (ADR 0047) opens the dialogue, and so does a keeper
   * with a trade who has a word for a quest, his own window a button away
   * (`direct` skips the dialogue then).
   */
  private openNpc(npcId: string, direct = false): void {
    this.visitingNpc = undefined;
    this.hideMarker();
    if (this.room === undefined) return;
    const npc = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(npcId);
    const mobId = npc?.mobId;
    // The road's peddler (ADR 0093): his one lot, no dialogue.
    if (npc !== undefined && mobId === 'peddler' && this.encounters?.openPeddler(npc) === true) {
      this.questClient?.closeDialog();
      this.bounties?.close();
      this.tutorPanel?.close();
      this.smithPanel?.close();
      this.runesmithPanel?.close();
      this.merchantPanel?.close();
      this.stashPanel?.close();
      this.fusionPanel?.close();
      this.alchemyPanel?.close();
      return;
    }
    this.encounters?.closePeddler();
    this.bounties?.close();
    if (!direct && npc !== undefined && mobId !== undefined && this.zone !== undefined) {
      const parsed = MobIdSchema.safeParse(mobId);
      // Ser Givrecoeur's advice is the camp's (ADR 0066): at the Company's
      // quarter he keeps the quests alone (ADR 0067).
      const label =
        mobId === TUTOR_MOB_ID && this.zone.id !== TUTORIAL_ZONE_ID
          ? undefined
          : SERVICE_LABELS[mobId];
      // A townsperson with no window of their own is the quests' (ADR 0047).
      const keeper =
        parsed.success &&
        MOBS[parsed.data].interactive &&
        label === undefined &&
        mobId !== AUCTION_SIGN_MOB_ID &&
        mobId !== STASH_MOB_ID &&
        mobId !== WANTED_BOARD_MOB_ID;
      const opened = this.questClient?.visit(
        {
          id: npcId,
          mob: mobId,
          biome: biomeAt(this.zone, npc),
          zone: this.zone.id,
          ...(label === undefined
            ? {}
            : {
                service: {
                  label,
                  open: () => {
                    this.openNpc(npcId, true);
                  },
                },
              }),
        },
        keeper,
      );
      if (opened === true) {
        this.tutorPanel?.close();
        this.smithPanel?.close();
        this.runesmithPanel?.close();
        this.merchantPanel?.close();
        this.stashPanel?.close();
        this.stashPanel?.close();
        this.stashPanel?.close();
        this.fusionPanel?.close();
        this.alchemyPanel?.close();
        return;
      }
    }
    // A village under a world event's attack (ADR 0050): its townsfolk hide
    // behind their stalls, the server would not serve anyway.
    if (
      npc !== undefined &&
      mobId !== undefined &&
      (SERVICE_LABELS[mobId] !== undefined ||
        mobId === AUCTION_SIGN_MOB_ID ||
        mobId === STASH_MOB_ID) &&
      this.worldEvents?.besieges(npc) === true
    ) {
      this.refusals.show(
        'Les gobelins attaquent le village : personne ne sert tant qu’ils sont là !',
      );
      return;
    }
    this.questClient?.closeDialog();
    // The signpost's hall sounds its own opening, once the room lets us in.
    if (mobId !== undefined && isInteractiveMob(mobId) && mobId !== AUCTION_SIGN_MOB_ID)
      cue('sfx.open');
    this.tutorPanel?.close();
    if (mobId === TUTOR_MOB_ID) {
      this.tutorId = npcId;
      this.smithPanel?.close();
      this.runesmithPanel?.close();
      this.merchantPanel?.close();
      this.stashPanel?.close();
      this.stashPanel?.close();
      this.fusionPanel?.close();
      this.alchemyPanel?.close();
      this.tutorPanel?.open();
    } else if (mobId === SMITH_MOB_ID) {
      this.smithId = npcId;
      this.runesmithPanel?.close();
      this.merchantPanel?.close();
      this.stashPanel?.close();
      this.stashPanel?.close();
      this.fusionPanel?.close();
      this.alchemyPanel?.close();
      this.smithPanel?.open();
    } else if (mobId === RUNESMITH_MOB_ID) {
      this.runesmithId = npcId;
      this.smithPanel?.close();
      this.merchantPanel?.close();
      this.stashPanel?.close();
      this.stashPanel?.close();
      this.fusionPanel?.close();
      this.alchemyPanel?.close();
      this.runesmithPanel?.open();
    } else if (mobId === MERCHANT_MOB_ID) {
      this.merchantId = npcId;
      this.smithPanel?.close();
      this.runesmithPanel?.close();
      this.fusionPanel?.close();
      this.alchemyPanel?.close();
      this.merchantPanel?.open();
    } else if (mobId === SMELTER_MOB_ID) {
      this.openSmelter(npcId);
    } else if (mobId === ALCHEMIST_MOB_ID) {
      this.openAlchemist(npcId);
    } else if (mobId === STASH_MOB_ID) {
      this.openStash(npcId);
    } else if (mobId === WANTED_BOARD_MOB_ID) {
      this.openBoard(npcId);
    } else if (mobId === AUCTION_SIGN_MOB_ID) {
      // The signpost only points the way: the hall's own door opens the house.
      const sign = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(npcId);
      const hall =
        sign === undefined || this.zone === undefined
          ? undefined
          : auctionHouseNear(this.zone, sign);
      if (hall !== undefined) this.visitAuction(hall);
    }
  }

  /** A wanted board (ADR 0096): one window at a time; the server sends the day's bounties. */
  private openBoard(boardId: string): void {
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.stashPanel?.close();
    this.fusionPanel?.close();
    this.alchemyPanel?.close();
    this.bounties?.open(boardId);
  }

  /** While a board's window is open: whether the player stands by it, as the server will judge. */
  private updateBoardReach(): void {
    const boardId = this.bounties?.boardId;
    if (boardId === undefined || this.room === undefined) return;
    const board = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(boardId);
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    if (me === undefined) return;
    if (
      board === undefined ||
      Math.max(Math.abs(me.x - board.x), Math.abs(me.y - board.y)) > BOUNTY_REACH
    )
      this.bounties?.close();
  }

  /** The account's chest (ADR 0079): one window at a time; the server sends what it holds. */
  private openStash(chestId: string): void {
    this.stashId = chestId;
    this.smithPanel?.close();
    this.runesmithPanel?.close();
    this.merchantPanel?.close();
    this.fusionPanel?.close();
    this.alchemyPanel?.close();
    this.stashPanel?.open();
    this.updateStashReach();
    this.room?.send('stash_open', {});
  }

  /** While the chest's window is open: whether the player stands by it, as the server will judge. */
  private updateStashReach(): void {
    if (this.stashPanel?.isOpen !== true || this.room === undefined) return;
    const chest = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(this.stashId ?? '');
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.stashPanel,
      chest !== undefined && me !== undefined && withinStashReach({ x: me.x, y: me.y }, [chest]),
    );
  }

  /** While the merchant's window is open: whether the player stands by him. */
  private updateMerchantReach(): void {
    if (this.merchantPanel?.isOpen !== true || this.room === undefined) return;
    const merchant = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(
      this.merchantId ?? '',
    );
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.merchantPanel,
      merchant !== undefined &&
        me !== undefined &&
        withinMerchantReach({ x: me.x, y: me.y }, [merchant]),
    );
  }

  /** While the smith's window is open: whether the player stands by him, as the server will judge. */
  private updateSmithReach(): void {
    if (this.smithPanel?.isOpen !== true || this.room === undefined) return;
    const smith = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(this.smithId ?? '');
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.smithPanel,
      smith !== undefined && me !== undefined && withinSmithReach({ x: me.x, y: me.y }, [smith]),
    );
  }

  /** While the runesmith's window is open: whether the player stands by him. */
  private updateRunesmithReach(): void {
    if (this.runesmithPanel?.isOpen !== true || this.room === undefined) return;
    const smith = (this.room.state.mobs as NetMap<NetMob> | undefined)?.get(this.runesmithId ?? '');
    const me = (this.room.state.players as NetMap<NetPlayer> | undefined)?.get(this.room.sessionId);
    this.followReach(
      this.runesmithPanel,
      smith !== undefined &&
        me !== undefined &&
        withinRunesmithReach({ x: me.x, y: me.y }, [smith]),
    );
  }

  /** The other player under the pointer; a sprite also rises a tile above its feet. */
  private playerUnder(pointer: Phaser.Input.Pointer): string | undefined {
    const target = this.tileUnder(pointer);
    return this.playerAt(target) ?? this.playerAt({ x: target.x, y: target.y + 1 });
  }

  private clientPointOf(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    const event = pointer.event;
    const at = 'clientX' in event ? event : event.changedTouches[0];
    return { x: at?.clientX ?? 0, y: at?.clientY ?? 0 };
  }

  private armPlayerPress(pointer: Phaser.Input.Pointer, name: string): void {
    this.playerPress?.timer.remove();
    const from = this.clientPointOf(pointer);
    const timer = this.time.delayedCall(PLAYER_PRESS_MS, () => {
      this.playerPress = undefined;
      this.social?.openPlayerMenu(name, from.x, from.y);
    });
    this.playerPress = { name, from, timer };
  }

  /** The press ends as a tap, or drifts into a walk: what the finger meant for the game. */
  private releasePlayerPress(pointer: Phaser.Input.Pointer, follow: boolean): void {
    const press = this.playerPress;
    if (press === undefined) return;
    press.timer.remove();
    this.playerPress = undefined;
    const walk = this.requestMove(pointer);
    this.heldWalk = follow && walk !== undefined ? { since: this.time.now, tile: walk } : undefined;
  }

  /** Name of another living player standing on `tile`. */
  private playerAt(tile: { x: number; y: number }): string | undefined {
    if (this.room === undefined) return undefined;
    let found: string | undefined;
    const players = this.room.state.players as NetMap<NetPlayer> | undefined;
    players?.forEach((player, sessionId) => {
      if (sessionId === this.room?.sessionId || found !== undefined || player.hp <= 0) return;
      if (player.x === tile.x && player.y === tile.y) found = player.name;
    });
    return found;
  }

  private mobAt(tile: { x: number; y: number }): string | undefined {
    if (this.room === undefined) return undefined;
    let found: string | undefined;
    const mobs = this.room.state.mobs as NetMap<NetMob> | undefined;
    mobs?.forEach((mob, id) => {
      const parsed = MobIdSchema.safeParse(mob.mobId);
      if (parsed.success && MOBS[parsed.data].peaceful) return;
      if (found === undefined && mob.x === tile.x && mob.y === tile.y && mob.hp > 0) found = id;
    });
    return found;
  }

  /** A damage number rising from the target's head, in the bitmap font; a crit's twice as big, in gold. */
  private floatDamage(key: string, amount: number, crit = false): void {
    if (!preference('damageNumbers')) return;
    if (crit) this.floatText(key, `${String(amount)}!`, CRIT_COLOR, 900, CRIT_SCALE);
    else this.floatText(key, String(amount), 0xff5c5c, 600);
  }

  /**
   * Any short text rising from an entity's head and fading, on whole pixels.
   * `scale` is a whole number, so a bigger text is still made of square pixels.
   */
  private floatText(
    key: string,
    value: string,
    color: number,
    durationMs: number,
    scale = 1,
  ): void {
    const font = this.manifest.sheets['font.pixel'];
    const origin = this.rendered.get(key);
    if (font === undefined || origin === undefined) return;
    const text = new PixelText({
      scene: this,
      atlasKey: font.atlas,
      fontKey: font.prefix,
      color,
      outlineColor: 0x12121c,
    });
    text.setText(value);
    text.setDepth(20_000);
    text.container.setScale(scale);
    const startY = Math.round(origin.y) - 44 - (scale - 1) * 4;
    text.setPosition(Math.round(origin.x), startY);
    // Whole-pixel steps: the tween drives a counter, the text snaps to it.
    const state = { y: startY };
    this.tweens.add({
      targets: state,
      y: startY - 10 * scale,
      duration: durationMs,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        text.setPosition(Math.round(origin.x), Math.round(state.y));
      },
      onComplete: () => {
        text.destroy();
      },
    });
  }

  /** The dragon's marks, and their names, as the player's story has them (ADR 0067). */
  private followStory(): void {
    const log = this.questClient?.log;
    if (log === undefined || !log.known || this.zone === undefined) return;
    this.beforeTheFire?.follow(log);
    for (const landmark of this.zone.landmarks) {
      if (landmark.before === undefined) continue;
      this.worldMap?.renamePlace(landmark.name, storyName(landmark, log));
    }
  }

  /**
   * Name the place the player stands in — the village when inside one, the
   * biome otherwise — on the minimap, and once in the chat on crossing into
   * it, so the world has names and a player knows where they are going.
   */
  private announcePlace(me: { x: number; y: number }): void {
    const zone = this.zone;
    if (zone === undefined) return;
    // Asked every refresh; only a change of place changes the tune.
    playMusic(this.worldEvents?.musicAt(me) ?? musicFor(zone, me));
    // The innermost place wins: the Company's bastion inside Havrebourg
    // (ADR 0067) is announced on entering it, and stepping back out into the
    // village is not an arrival.
    const village = zone.landmarks
      .filter((landmark) => Math.hypot(landmark.x - me.x, landmark.y - me.y) <= landmark.radius)
      .reduce<(typeof zone.landmarks)[number] | undefined>(
        (inner, landmark) =>
          inner === undefined || landmark.radius < inner.radius ? landmark : inner,
        undefined,
      );
    const biome = biomeAt(zone, me);
    this.weather?.setBiome(biome);
    const name =
      (village === undefined ? undefined : storyName(village, this.questClient?.log)) ??
      (biome === undefined ? zone.name : BIOMES[biome].name);
    if (name === this.place) return;
    const first = this.place === '';
    // Stepping out of the Company's bastion is not arriving in Havrebourg.
    const backFromBastion = this.place === COMPANY_QUARTER;
    this.place = name;
    // A village takes the levels of the biome it stands in.
    const level = biome === undefined ? undefined : BIOMES[biome].level;
    this.minimap?.setPlace(name, level);
    // The bastion is signed like a village: the heart of the story (ADR 0067).
    const signed =
      (village?.kind === 'village' && !backFromBastion) || village?.name === COMPANY_QUARTER;
    // Behind the teleport's flames, the sign waits until they have parted.
    if (signed) this.villageSign?.show(name, this.veil?.shown === true ? 900 : 0);
    if (!first && !backFromBastion) {
      if (signed) cue('sfx.discover');
      this.placeBanner.show(placeLine(name, level));
    }
  }

  override update(time: number, delta: number): void {
    const room = this.room;
    if (room === undefined) return;
    this.nightShade?.update(time);
    this.weather?.update(time);
    this.updateNpcVisit();
    this.updatePortalVisit();
    this.updateAuctionVisit();
    this.updateChestVisit();
    this.updateRoadChestVisit();
    this.updateSmithReach();
    this.updateRunesmithReach();
    this.updateSmelterReach();
    this.updateAlchemistReach();
    this.updateMerchantReach();
    this.updatePeddlerReach();
    this.updateBoardReach();
    this.updateStashReach();
    this.updateTutorReach();
    this.updateDialogReach();
    if (room.state.online !== undefined) this.onlineCount?.set(room.state.online);
    this.refreshTouchMount();
    this.followHeldWalk(time);
    this.followKeyboardWalk();
    const me = (room.state.players as NetMap<NetPlayer> | undefined)?.get(room.sessionId);
    if (me !== undefined) this.quickPotion?.setHealth(me.hp, me.maxHp);

    const seen = new Set<string>();
    // Every synchronised map is absent until its first entry arrives, this one
    // included: a frame can run between joining and the first patch.
    const players = room.state.players as NetMap<NetPlayer> | undefined;
    players?.forEach((player, sessionId) => {
      seen.add(sessionId);
      // The local player is drawn where its predicted walk is (ADR 0045),
      // everyone else where the server last put them.
      const local =
        sessionId === room.sessionId ? this.localMovement.frame(time, player) : undefined;
      if (sessionId === room.sessionId) this.lowHealth?.update(player.hp, player.maxHp, time);
      const position = this.drawEntity(
        sessionId,
        {
          kind: 'player',
          bodyKey: `body.${player.classId}`,
          name: player.name,
          x: player.x,
          y: player.y,
          dir: local?.dir ?? player.dir,
          anim: local?.anim ?? player.anim,
          hp: player.hp,
          maxHp: player.maxHp,
          appearance: {
            helmet: player.helmet,
            chest: player.chest,
            weapon: player.weapon,
            offhand: player.offhand,
            gloves: player.gloves,
            boots: player.boots,
            wings: player.wings,
          },
          upgrades: {
            helmet: player.helmetLevel,
            chest: player.chestLevel,
            weapon: player.weaponLevel,
            offhand: player.offhandLevel,
            gloves: player.glovesLevel,
            boots: player.bootsLevel,
          },
          aura: player.aura,
          rarities: unpackRarities(player.rarities),
          look: {
            hair: player.hair,
            eyes: player.eyes,
            hairStyle: player.hairStyle,
            skin: player.skin,
            body: player.body,
          },
          mount: player.mount,
          level: player.level,
          gm: player.gm,
          vanished: player.vanished,
          guild: player.guild,
          title: player.title,
          away: player.away,
        },
        delta,
        local === undefined ? undefined : { x: local.px, y: local.py },
      );
      if (position !== undefined) {
        this.footstepTrails?.follow(sessionId, player.trail, position, time);
      }
      if (position !== undefined && sessionId === room.sessionId && this.editor?.active !== true) {
        const jolt = this.juice.offset();
        const lean = this.cinematics?.cameraOffset() ?? { x: 0, y: 0 };
        this.cameras.main.centerOn(
          Math.round(position.x) + jolt.x + lean.x,
          Math.round(position.y) + jolt.y + lean.y,
        );
        this.moveMarker?.arrive(player);
        this.veil?.update();
        this.cinematics?.update();
      }
    });
    const mobs = room.state.mobs as NetMap<NetMob> | undefined;
    mobs?.forEach((mob, id) => {
      // Mob ids and session ids live in different namespaces; the prefix keeps
      // a rat named like a session from stealing its sprite.
      const key = `mob:${id}`;
      seen.add(key);
      const parsed = MobIdSchema.safeParse(mob.mobId);
      const definition = parsed.success ? MOBS[parsed.data] : undefined;
      this.drawEntity(
        key,
        {
          kind: 'mob',
          ...(definition !== undefined && definition.tilesPerSecond < STEADY_GLIDE_BELOW_TPS
            ? { glideTilesPerSecond: definition.tilesPerSecond }
            : {}),
          bodyKey: definition?.bodyKey ?? `body.${mob.mobId}`,
          name: definition?.nameplate === false ? '' : (definition?.name ?? mob.mobId),
          peaceful: definition?.peaceful === true,
          interactive: definition?.interactive === true,
          ...(definition?.interactive === true && this.zone !== undefined
            ? {
                questMarker:
                  this.questClient?.markerOf({
                    mob: mob.mobId,
                    biome: biomeAt(this.zone, mob),
                    zone: this.zone.id,
                  }) ?? null,
              }
            : {}),
          rarity: definition?.rarity,
          boss: definition?.rank === 'zone-boss',
          rare: isRareMob(mob.mobId),
          passive: mob.passive === true,
          hunted:
            definition?.peaceful !== true &&
            (this.questClient?.log.huntedMobs().has(mob.mobId) ?? false),
          // A townsperson drawn as a player (the tutor): his pieces, their
          // level and the halo they earn, from the game data.
          ...outfitOf(definition?.outfit),
          x: mob.x,
          y: mob.y,
          dir: mob.dir,
          anim: mob.anim,
          hp: mob.hp,
          maxHp: mob.maxHp,
          // Hidden with the name: a monster without a plate has no level to show.
          level: definition?.nameplate === false ? undefined : definition?.level,
        },
        delta,
      );
    });

    this.aerie?.update();

    if (this.markedMobId !== undefined) {
      const marked = mobs?.get(this.markedMobId);
      if (marked === undefined || marked.hp <= 0) this.hideMarker();
      else this.moveMarker?.place({ x: marked.x, y: marked.y });
    }
    this.targetFrame?.show(this.targetFrameState(mobs));

    // Pets: an entity each, drawn like a small mob with no name and no bar.
    const companions = room.state.companions as NetMap<NetCompanion> | undefined;
    companions?.forEach((pet, ownerId) => {
      // The body is read once, when the view spawns: another pet is another view.
      const key = `pet:${ownerId}:${pet.companionId}`;
      seen.add(key);
      const definition = getCompanion(pet.companionId);
      this.drawEntity(
        key,
        {
          kind: 'pet',
          bodyKey: definition?.bodyKey ?? `body.${pet.companionId}`,
          name: '',
          x: pet.x,
          y: pet.y,
          dir: pet.dir,
          anim: pet.anim,
          hp: 1,
          maxHp: 1,
        },
        delta,
      );
      // The pet stands beside its master, but its drawn row trails theirs
      // while walking (the master is predicted, the pet interpolated): by the
      // painter's order alone it would dip under the master's aura at every step.
      const drawn = this.rendered.get(key);
      const master = this.rendered.get(ownerId);
      if (drawn !== undefined && master !== undefined && Math.abs(drawn.y - master.y) < TILE_SIZE) {
        this.views.get(key)?.setDepth(Math.max(drawn.y, master.y) + 0.5);
      }
    });

    if (this.minimap !== undefined && time - this.minimapAt >= MINIMAP_REFRESH_MS) {
      this.minimapAt = time;
      const dots: MinimapDot[] = [];
      const seen: { name: string; x: number; y: number }[] = [];
      players?.forEach((player, sessionId) => {
        if (sessionId !== room.sessionId)
          seen.push({ name: player.name, x: player.x, y: player.y });
        dots.push({
          x: player.x,
          y: player.y,
          kind: sessionId === room.sessionId ? 'self' : 'player',
        });
      });
      mobs?.forEach((mob) => {
        const parsed = MobIdSchema.safeParse(mob.mobId);
        if (parsed.success && MOBS[parsed.data].peaceful) return;
        // A rare is in the state only within the area of interest: marked
        // where the player could see it anyway, never further (ADR 0094).
        if (mob.hp > 0) dots.push({ x: mob.x, y: mob.y, kind: isRareMob(mob.mobId) ? 'rare' : 'mob' });
      });
      for (const friend of this.friendPositions) {
        dots.push({ x: friend.x, y: friend.y, kind: 'friend' });
      }
      for (const target of this.questTargets()) {
        dots.push({ x: target.x, y: target.y, kind: 'quest' });
      }
      const event = this.worldEvents?.current;
      if (event !== undefined && event !== null)
        dots.push({ x: event.x, y: event.y, kind: 'event' });
      for (const encounter of this.encounters?.encounters ?? []) {
        dots.push({ x: encounter.x, y: encounter.y, kind: 'encounter', faded: encounter.done });
      }
      this.minimap.draw(dots, this.questMarkers());
      this.worldMap?.setPlayers(mapPlayers(seen, this.adminPlayers, this.friendPositions));
      const me = players?.get(room.sessionId);
      if (me !== undefined) {
        this.announcePlace(me);
        this.worldMap?.setSelf(me);
      }
      const boss = me === undefined ? null : (this.aerie?.bar() ?? this.bossNear(me, mobs));
      this.barBossId = boss?.mobId;
      this.bossBar?.show(boss?.bar ?? this.bossAwaited());
    }

    for (const [key, view] of this.views) {
      if (seen.has(key)) continue;
      view.destroy();
      this.views.delete(key);
      this.footstepTrails?.forget(key);
      this.rendered.delete(key);
      this.lastAnims.delete(key);
    }
    this.footstepTrails?.draw(time);

    const dropsSeen = new Set<string>();
    const small = this.manifest.sheets['font.pixel-small'];
    // An empty map is not instantiated by the decoder until its first entry
    // arrives, so a zone with nothing on the ground has no `drops` yet.
    const drops = room.state.drops as NetMap<NetDrop> | undefined;
    const perTile = new Map<string, number>();
    // The drop a click would pick up (SHE-36): the mouse only, a finger has no hover.
    const pointer = this.input.activePointer;
    const hoveredDrop =
      pointer.wasTouch || this.editor?.active === true
        ? undefined
        : this.dropAt(this.tileUnder(pointer));
    drops?.forEach((drop, id) => {
      dropsSeen.add(id);
      const tileKey = `${String(drop.x)}:${String(drop.y)}`;
      const stackIndex = perTile.get(tileKey) ?? 0;
      perTile.set(tileKey, stackIndex + 1);
      let view = this.dropViews.get(id);
      if (view === undefined && small !== undefined) {
        const icon = this.manifest.sprites[getItem(drop.templateId)?.icon ?? ''];
        view = new DropView({
          scene: this,
          templateId: drop.templateId,
          quantity: drop.quantity,
          font: { atlasKey: small.atlas, fontKey: small.prefix },
          icon: icon === undefined ? undefined : { atlasKey: icon.atlas, frame: icon.frame },
        });
        this.dropViews.set(id, view);
        if (this.dropsPrimed) {
          // A rare find is heard and seen landing (ADR 0049); a common one just thuds.
          const rare = view.rarity !== 'common';
          cueAt(rare ? 'sfx.loot-rare' : 'sfx.drop', this.tileCentre(drop), this.listener());
          if (rare) {
            const { x } = this.tileCentre(drop);
            lootBurst(this, view.rarity, { x, y: drop.y * TILE_SIZE + TILE_SIZE - 2 });
          }
        }
      }
      // Re-placed every frame: when a neighbour is picked up the rest close ranks.
      view?.setTile(drop.x, drop.y, stackIndex);
      view?.setBetter(this.isBetterDrop(drop.templateId));
      view?.setFading(drop.fading, time);
      view?.setHovered(id === hoveredDrop);
    });
    for (const [id, view] of this.dropViews) {
      if (dropsSeen.has(id)) continue;
      view.destroy();
      this.dropViews.delete(id);
    }
    if (players?.has(room.sessionId) === true) this.dropsPrimed = true;
    const den = this.baitDen(players?.get(room.sessionId), mobs);
    this.denGuide?.show(den, BAIT_REACH * TILE_SIZE, time);
    // One arrow at a time: the bait's, then the quests'.
    this.questPointer?.point(
      den === undefined ? this.questWay(players?.get(room.sessionId)) : undefined,
      time,
    );
  }

  /**
   * The green arrow over a piece on the ground: it lies there at +0 and
   * without affixes (they are rolled when it is picked up, ADR 0057).
   */
  private isBetterDrop(templateId: string): boolean {
    const known = this.betterDrops.get(templateId);
    if (known !== undefined) return known;
    const template = getItem(templateId);
    const better =
      template !== undefined &&
      this.inventory !== undefined &&
      isBetterThanWorn(
        { template, upgradeLevel: 0 },
        { level: this.progress?.level ?? 1, classId: this.session.character.classId },
        this.inventory.equipped,
      );
    this.betterDrops.set(templateId, better);
    return better;
  }

  /**
   * Where the arrow leads for the quests under way (ADR 0047), in world
   * pixels: the nearest camp or named place one of them asks for, by its
   * cave when it stands on a summit; nothing without such a quest.
   */
  private questWay(me: NetPlayer | undefined): { x: number; y: number } | undefined {
    const zone = this.zone;
    if (me === undefined || zone === undefined) return undefined;
    let best: { x: number; y: number } | undefined;
    let bestFar = Infinity;
    for (const target of this.questTargets()) {
      if (target.guide !== true) continue;
      const way = wayTo(zone, target, me);
      const far = Math.hypot(way.x - me.x, way.y - me.y);
      if (far < bestFar) {
        best = way;
        bestFar = far;
      }
    }
    if (best === undefined) return undefined;
    // The tip just over the tile, as the tutorial's over what it shows.
    return { x: best.x * TILE_SIZE + TILE_SIZE / 2, y: best.y * TILE_SIZE - 2 };
  }

  /**
   * The den to lay a quest's bait by (ADR 0047), in world pixels: while a
   * bait lent by a quest is in the bag and that quest still asks for its
   * catch, the nearest den of its creature in the quest's region; nothing
   * once the catch is out, or without a bait.
   */
  private baitDen(
    me: NetPlayer | undefined,
    mobs: NetMap<NetMob> | undefined,
  ): { x: number; y: number } | undefined {
    const log = this.questClient?.log;
    const zone = this.zone;
    const bag = this.inventory;
    if (log === undefined || zone === undefined || bag === undefined || me === undefined) return;
    for (const entry of log.entries()) {
      for (const grant of entry.quest.grants) {
        const bait = getItem(grant.templateId)?.bait;
        if (bait === undefined) continue;
        if (!bag.bag.some((slot) => slot.item.templateId === grant.templateId)) continue;
        const wanted = entry.quest.objectives.some((objective, index) => {
          const progress = entry.progress[index];
          return (
            objective.kind === 'kill' &&
            objective.mobs.some((mob) => mob === bait.summons) &&
            progress !== undefined &&
            progress.value < progress.target
          );
        });
        if (!wanted) continue;
        const catchOut = { seen: false };
        mobs?.forEach((mob) => {
          if (mob.mobId === bait.summons && mob.hp > 0) catchOut.seen = true;
        });
        if (catchOut.seen) return undefined;
        let nearest: { x: number; y: number } | undefined;
        let best = Infinity;
        for (const den of zone.spawns) {
          if (den.mob !== bait.den || biomeAt(zone, den) !== entry.quest.biome) continue;
          const distance = Math.hypot(den.x - me.x, den.y - me.y);
          if (distance < best) {
            best = distance;
            nearest = den;
          }
        }
        return nearest === undefined ? undefined : this.tileCentre(nearest);
      }
    }
    return undefined;
  }

  /** Where the ear is: the local player, as drawn. */
  private listener(): { x: number; y: number } | undefined {
    return this.room === undefined ? undefined : this.rendered.get(this.room.sessionId);
  }

  /** An effect from an entity, by its view key, heard from where the player stands. */
  /**
   * The living boss in whose lair the player stands, for the bar at the top
   * of the screen: within its leash of its tile, as the server fights it.
   */
  /**
   * What the target frame shows: the hostile creature picked or fought, under
   * the boss bar when it is up; otherwise, near a boss, that boss's states
   * alone, under its bar, whether it is the target or not. Null for none.
   */
  private targetFrameState(mobs: NetMap<NetMob> | undefined): TargetFrameState | null {
    const shown = this.moveMarker?.shown;
    const picked =
      this.markedMobId !== undefined && (shown === 'attack' || shown === 'target')
        ? this.markedMobId
        : undefined;
    const bossId =
      this.barBossId !== undefined && mobs?.has(this.barBossId) ? this.barBossId : undefined;
    const target = picked === undefined ? undefined : this.hostileFrame(picked, mobs);
    if (target !== undefined && picked !== bossId) {
      return { ...target, belowBossBar: bossId !== undefined, statesOnly: false };
    }
    const boss = bossId === undefined ? undefined : this.hostileFrame(bossId, mobs);
    return boss === undefined ? null : { ...boss, belowBossBar: true, statesOnly: true };
  }

  private hostileFrame(
    mobId: string,
    mobs: NetMap<NetMob> | undefined,
  ): Omit<TargetFrameState, 'belowBossBar' | 'statesOnly'> | undefined {
    const mob = mobs?.get(mobId);
    const parsed = MobIdSchema.safeParse(mob?.mobId);
    if (mob === undefined || mob.hp <= 0 || !parsed.success) return undefined;
    const definition = MOBS[parsed.data];
    if (definition.peaceful) return undefined;
    return {
      mobId,
      name: definition.name,
      ...(!definition.nameplate || definition.level === undefined
        ? {}
        : { level: definition.level }),
      hp: mob.hp,
      maxHp: mob.maxHp,
      passive: mob.passive === true,
    };
  }

  /**
   * In a boss's dungeon while it lies dead (ADR 0090): its bar, empty, with
   * the seconds before it stands up. Gone once it lives again.
   */
  private bossAwaited(): BossBarState | null {
    const waking = this.bossWaking;
    if (waking === undefined || this.zone?.persistent !== true) return null;
    const parsed = MobIdSchema.safeParse(waking.bossId);
    if (!parsed.success || Date.now() > waking.at + 5000) return null;
    const definition = MOBS[parsed.data];
    return {
      name: definition.name,
      hp: 0,
      maxHp: definition.health,
      enraged: false,
      bonus: this.questClient?.bossBonusAvailable(waking.bossId) ?? false,
      wakesAt: waking.at,
    };
  }

  /** The boss near enough for its bar, and its instance id for the target frame. */
  private bossNear(
    me: { x: number; y: number },
    mobs: NetMap<NetMob> | undefined,
  ): { mobId: string; bar: BossBarState } | null {
    let found: { mobId: string; bar: BossBarState } | null = null;
    mobs?.forEach((mob, mobId) => {
      if (found !== null || mob.hp <= 0) return;
      const boss = bossOf(mob.mobId);
      const parsed = MobIdSchema.safeParse(mob.mobId);
      if (boss === undefined || !parsed.success) return;
      const definition = MOBS[parsed.data];
      if (Math.max(Math.abs(mob.x - me.x), Math.abs(mob.y - me.y)) > definition.leashRange + 2)
        return;
      found = {
        mobId,
        bar: {
          name: definition.name,
          hp: mob.hp,
          maxHp: mob.maxHp,
          enraged: mob.hp <= mob.maxHp * boss.enrage.at,
          bonus: this.questClient?.bossBonusAvailable(mob.mobId) ?? false,
        },
      };
    });
    return found;
  }

  private cueOn(sound: string, key: string): void {
    const at = this.rendered.get(key);
    if (at !== undefined) cueAt(sound, at, this.listener());
  }

  /** Whether the entity drawn under `key` is a monster that shoots (ADR 0055). */
  private shootsFrom(key: string): boolean {
    if (!key.startsWith('mob:')) return false;
    const mob = this.room?.state.mobs.get(key.slice(4));
    const kind = MobIdSchema.safeParse(mob?.mobId);
    return kind.success && MOBS[kind.data].ranged !== undefined;
  }

  private tileCentre(tile: { x: number; y: number }): { x: number; y: number } {
    return { x: tile.x * TILE_SIZE + TILE_SIZE / 2, y: tile.y * TILE_SIZE + TILE_SIZE / 2 };
  }

  /** A blow swung or a fall, heard as the animation starts, from anyone near. */
  private cueAnimation(key: string, entity: Entity, known: boolean): void {
    const previous = this.lastAnims.get(key);
    this.lastAnims.set(key, entity.anim);
    if (!known || previous === entity.anim || entity.kind === 'pet') return;
    // A shooter's shot has its own twang, from the `mob_shot` message.
    if (entity.anim === 'attack' && entity.peaceful !== true) {
      if (!this.shootsFrom(key)) this.cueOn('sfx.swing', key);
    }
    // The player's own fall has its lament, from the `death` message.
    else if (entity.anim === 'death' && entity.kind === 'mob') this.cueOn('sfx.mob-death', key);
  }

  /** Move one entity's sprite toward its tile and play its animation. Returns where it is drawn. */
  private drawEntity(
    key: string,
    entity: Entity,
    delta: number,
    /** Where to draw it instead, in pixels: the local player's predicted walk. */
    at?: { x: number; y: number },
  ): { x: number; y: number } | undefined {
    const known = this.views.has(key);
    const view = this.views.get(key) ?? this.spawnView(key, entity);
    if (view === undefined) return undefined;

    const targetX = entity.x * TILE_SIZE + TILE_SIZE / 2;
    const targetY = entity.y * TILE_SIZE + TILE_SIZE;
    const current = this.rendered.get(key) ?? { x: targetX, y: targetY };
    if (at !== undefined) {
      current.x = at.x;
      current.y = at.y;
    } else {
      if (Math.hypot(targetX - current.x, targetY - current.y) > SNAP_TILES * TILE_SIZE) {
        current.x = targetX;
        current.y = targetY;
      }
      if (entity.glideTilesPerSecond !== undefined) {
        const next = steadyGlide(
          current,
          { x: targetX, y: targetY },
          delta,
          entity.glideTilesPerSecond,
          TILE_SIZE,
        );
        current.x = next.x;
        current.y = next.y;
      } else {
        // Interpolate toward the authoritative tile rather than snapping: the
        // server moves in whole tiles, the eye wants continuous motion.
        const factor = Math.min(1, delta * INTERPOLATION_PER_MS);
        current.x += (targetX - current.x) * factor;
        current.y += (targetY - current.y) * factor;
      }
    }
    this.rendered.set(key, current);

    view.setPixelPosition(current.x, current.y);
    // Painter's order: whoever is lower on screen is nearer the camera.
    view.setDepth(current.y);
    if (entity.appearance !== undefined) view.setAppearance(entity.appearance);
    if (entity.upgrades !== undefined) view.setUpgrades(entity.upgrades, entity.aura ?? 0);
    if (entity.rarities !== undefined) view.setRarities(entity.rarities);
    // Players, and the townsperson drawn as one on his mount (the tutor).
    if (entity.mount !== undefined) {
      const mount = entity.mount === '' ? undefined : getCompanion(entity.mount);
      const sheet = mount === undefined ? undefined : this.manifest.sheets[mount.bodyKey];
      const changed = view.setMount(
        mount !== undefined && sheet !== undefined
          ? { atlas: sheet.atlas, prefix: sheet.prefix, seat: mount.seat, seatBack: mount.seatBack }
          : null,
      );
      // Getting on or off, seen happen: a puff of dust. Not for a rider who
      // just walked into view already mounted.
      if (changed && known) {
        puffSmoke(this, { x: Math.round(current.x), y: Math.round(current.y) }, current.y + 1);
        cueAt('sfx.mount', current, this.listener());
      }
    }
    if (entity.look !== undefined) {
      // Names to hexes without a schema pass: this runs for every drawn player, every frame.
      view.setLook({
        hair: hairHex(entity.look.hair),
        eyes: eyeHex(entity.look.eyes),
        hairStyle: entity.look.hairStyle,
        skin: skinHex(entity.look.skin),
        body: entity.look.body,
      });
    }
    this.cueAnimation(key, entity, known);
    if (key === this.room?.sessionId) {
      const cast = this.mountCast;
      view.setChannel(cast === null ? null : (this.time.now - cast.startedAt) / cast.durationMs);
    }
    view.play(entity.anim, this.toDirection(entity.dir));
    view.setHealth(entity.hp, entity.maxHp);
    if (entity.level !== undefined) view.setLevel(entity.level);
    if (entity.gm !== undefined) view.setGm(entity.gm);
    if (entity.vanished !== undefined) view.setVanished(entity.vanished);
    if (entity.guild !== undefined) view.setGuild(entity.guild);
    if (entity.title !== undefined) view.setTitle(titleOf(entity.title) ?? '');
    if (entity.away !== undefined) view.setAway(entity.away);
    if (entity.questMarker !== undefined) view.setQuestMarker(this.markerFrame(entity.questMarker));
    if (entity.hunted !== undefined) view.setHunted(entity.hunted);
    if (entity.rare !== undefined) view.setRare(entity.rare);
    return current;
  }

  /**
   * Where the quests under way lead (ADR 0047), recomputed only when the
   * journal moved; the world map follows.
   */
  private questTargets(): readonly QuestTarget[] {
    const log = this.questClient?.log;
    const zone = this.zone;
    if (log === undefined || zone === undefined) return [];
    // A wanted beast called out (ADR 0096) is led to like a quest's place.
    const stamp = `${String(log.stamp)}:${String(this.bounties?.stamp ?? 0)}`;
    if (this.questTargetCache?.stamp !== stamp) {
      const targets = log.targets(zone);
      const hunted = this.bounties?.target;
      if (hunted !== undefined) {
        targets.push({ questId: `bounty:${hunted.slot}`, x: hunted.x, y: hunted.y, guide: true });
      }
      this.questTargetCache = { stamp, targets };
      this.worldMap?.setQuests(this.questMarkers(), targets);
    }
    return this.questTargetCache.targets;
  }

  /**
   * The townsfolk's quest marks for the minimap (ADR 0047): a « ! » while
   * one offers a quest, a « ? » while one waits for the player, from the
   * zone's spawns so they show out of sight; recomputed when the journal moved.
   */
  private questMarkers(): readonly MinimapMarker[] {
    const log = this.questClient?.log;
    const zone = this.zone;
    if (log === undefined || zone === undefined) return [];
    if (this.questMarkerCache?.stamp !== log.stamp) {
      const markers: MinimapMarker[] = [];
      for (const spawn of zone.spawns) {
        const parsed = MobIdSchema.safeParse(spawn.mob);
        if (!parsed.success || !MOBS[parsed.data].interactive) continue;
        const marker = log.markerOf({ mob: spawn.mob, biome: biomeAt(zone, spawn), zone: zone.id });
        if (marker !== null) markers.push({ x: spawn.x, y: spawn.y, marker });
      }
      this.questMarkerCache = { stamp: log.stamp, markers };
    }
    return this.questMarkerCache.markers;
  }

  /** The effect frame of a quest marker (ADR 0047): `effects/quest-<marker>/0`. */
  private markerFrame(marker: QuestMarker | null): { atlasKey: string; frame: string } | null {
    if (marker === null) return null;
    const sheet = this.manifest.sheets[`effect.quest-${marker}`];
    return sheet === undefined ? null : { atlasKey: sheet.atlas, frame: `${sheet.prefix}/idle/0` };
  }

  private toDirection(value: string): Direction {
    return (DIRECTIONS as readonly string[]).includes(value) ? (value as Direction) : 'south';
  }

  private spawnView(key: string, entity: Entity): CharacterView | undefined {
    const sheet = this.manifest.sheets[entity.bodyKey];
    if (sheet === undefined) return undefined;
    // Another class's body is left out of the boot (`otherBodyAtlases`): the
    // first one seen fetches it, and its owner appears once it has landed.
    if (!this.textures.exists(sheet.atlas)) {
      if (!this.requestedAtlases.has(sheet.atlas)) {
        this.requestedAtlases.add(sheet.atlas);
        void loadLazyAtlases(this, this.manifest, [sheet.atlas]);
      }
      return undefined;
    }
    const texture = this.textures.get(sheet.atlas);

    const font = this.manifest.sheets['font.pixel'];
    const small = this.manifest.sheets['font.pixel-small'];
    if (font === undefined) return undefined;

    const halo = this.manifest.sheets['effect.upgrade-glow'];
    const view = new CharacterView({
      scene: this,
      bodyKey: sheet.prefix,
      atlasKey: sheet.atlas,
      name: entity.name,
      self: key === this.room?.sessionId,
      hasFrame: (frame) => texture.has(frame),
      halo:
        halo === undefined
          ? undefined
          : {
              atlasKey: halo.atlas,
              clip: `${halo.prefix}/idle`,
              firstFrame: `${halo.prefix}/idle/0`,
            },
      font: { atlasKey: font.atlas, fontKey: font.prefix },
      // Every plate is small, so names do not bury the crowd; the colour sorts
      // friend from foe before a single letter is read: players pale, enemies
      // red (a grey-blue for those that only fight back), townsfolk a warm straw, someone to walk past rather than fight,
      // and a townsperson who answers a click a bright green.
      label:
        small !== undefined
          ? {
              atlasKey: small.atlas,
              fontKey: small.prefix,
              glyphWidth: SMALL_GLYPH_WIDTH,
              glyphHeight: SMALL_GLYPH_HEIGHT,
              color:
                entity.kind === 'player'
                  ? 0xe6e6f0
                  : entity.interactive === true
                    ? 0x8cf07a
                    : entity.peaceful === true
                      ? 0xf0dc9a
                      : entity.boss === true
                        ? BOSS_NAME_COLOUR
                        : entity.rare === true
                          ? RARE_COLOUR
                          : entity.rarity !== undefined && entity.rarity !== 'common'
                          ? ELITE_NAME_COLOURS[entity.rarity]
                          : entity.passive === true
                            ? PASSIVE_NAME_COLOUR
                            : 0xff5c5c,
              overHead: entity.kind !== 'player',
            }
          : undefined,
    });
    this.views.set(key, view);
    return view;
  }

  private showStatus(message: string): void {
    const font = this.manifest.sheets['font.pixel'];
    if (font === undefined) return;

    this.status ??= new PixelText({
      scene: this,
      atlasKey: font.atlas,
      fontKey: font.prefix,
      color: 0xe6e6f0,
      outlineColor: 0x12121c,
      fixedToCamera: true,
    });
    this.status.setText(message);
    this.status.setDepth(10_000);
    this.status.setVisible(true);
    this.placeStatus();
  }

  /**
   * The zone's bounds, a map smaller than the view (a dungeon) centred in it.
   * On a touch screen they reach below the map by the bottom HUD's height, so
   * a cave's way out, three rows from the edge, is not left under it (SHE-79).
   */
  private fitCameraBounds(): void {
    if (this.zone === undefined) return;
    const { width, height } = this.scale.gameSize;
    const shown = this.game.canvas.getBoundingClientRect().height;
    const inset = isTouch() && shown > 0 ? (bottomHudHeight() * height) / shown : 0;
    const bounds = cameraBounds(
      this.zone.width * TILE_SIZE,
      this.zone.height * TILE_SIZE,
      width,
      height,
      inset,
    );
    this.cameras.main.setBounds(bounds.x, bounds.y, bounds.width, bounds.height);
  }

  /** Centred on screen, on whole pixels, and re-placed whenever the view resizes. */
  private placeStatus(): void {
    const { width, height } = this.scale.gameSize;
    this.status?.setPosition(Math.round(width / 2), Math.round(height / 2));
  }

  private hideStatus(): void {
    this.status?.setVisible(false);
  }
}

/** Re-exported so the boot scene can hand the cache key over unchanged. */
export { MANIFEST_CACHE_KEY, RENDER_ZOOM };

/** Whether a player stands touching a chest, as the server judges it (diagonals count). */
function touches(chest: ChestPlacement, at: { x: number; y: number }): boolean {
  return Math.max(Math.abs(at.x - chest.x), Math.abs(at.y - chest.y)) <= 1;
}

/** The tile of a footprint nearest to `at`: how far a chest or an arch is, as the server measures it. */
function closestTileOf(
  box: { x0: number; y0: number; x1: number; y1: number },
  at: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: Math.min(Math.max(at.x, box.x0), box.x1),
    y: Math.min(Math.max(at.y, box.y0), box.y1),
  };
}
